import axios from "axios";
import { buildContributorKeyAlias, removeLocalVaultPrivateKey, signJsonWithPreferredLocalVault, storeLocalVaultPrivateKey } from "./localVault";

const ENV_API_BASE_URL = String(import.meta.env.VITE_API_BASE_URL || "").trim();
const CURRENT_HOSTNAME = typeof window !== "undefined"
  ? String(window.location.hostname || "").toLowerCase()
  : "";
const IS_LOCAL_DEV = Boolean(
  import.meta.env.DEV
  && typeof window !== "undefined"
  && ["localhost", "127.0.0.1"].includes(CURRENT_HOSTNAME)
);
const IS_TAURI_DESKTOP = Boolean(
  !import.meta.env.DEV
  && typeof window !== "undefined"
  && (CURRENT_HOSTNAME === "tauri.localhost" || typeof window.__TAURI_INTERNALS__ !== "undefined")
);
const PRODUCTION_API_BASE_URL = "https://dissaax.com/api_erp";
const DEFAULT_LOCAL_API_BASE_URL = "http://localhost/api_erp";
const DEFAULT_DESKTOP_API_BASE_URL = DEFAULT_LOCAL_API_BASE_URL;
const NORMALIZED_ENV_API_BASE_URL = ENV_API_BASE_URL.replace(/\/+$/, "");

function isLikelyFrontendDevServerApiBase(url) {
  const normalized = String(url || "").trim().replace(/\/+$/, "");
  if (!normalized) return false;

  // Evita usar base apontando para host local com porta de frontend (ex.: 4028).
  // Nesses casos, a API deve continuar no Apache/XAMPP sem porta (localhost/api_erp).
  const match = normalized.match(/^https?:\/\/(localhost|127\.0\.0\.1):(\d+)(?:\/api_erp)?$/i);
  if (!match) return false;

  const port = Number(match[2] || 0);
  if (!Number.isFinite(port) || port <= 0) return false;

  return port !== 80 && port !== 443;
}

const RESOLVED_LOCAL_WEB_API_BASE_URL = isLikelyFrontendDevServerApiBase(NORMALIZED_ENV_API_BASE_URL)
  ? DEFAULT_LOCAL_API_BASE_URL
  : (NORMALIZED_ENV_API_BASE_URL || DEFAULT_LOCAL_API_BASE_URL);

const DESKTOP_API_BASE_URLS = [
  ENV_API_BASE_URL || DEFAULT_DESKTOP_API_BASE_URL,
  DEFAULT_LOCAL_API_BASE_URL,
  PRODUCTION_API_BASE_URL,
  "http://localhost/api_erp",
];
const AGT_REQUEST_TIMEOUT_MS = 25_000;
// Em desenvolvimento local, força same-origin para usar proxy do Vite e evitar CORS do browser.
// Em desktop Tauri, usa ENV_API_BASE_URL (normalmente localhost) ou fallback para localhost
const API_BASE_URL = IS_LOCAL_DEV
  ? RESOLVED_LOCAL_WEB_API_BASE_URL
  : (ENV_API_BASE_URL || (IS_TAURI_DESKTOP ? DEFAULT_DESKTOP_API_BASE_URL : PRODUCTION_API_BASE_URL));


const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const ERP_SESSION_EXPIRED_EVENT = "angolabi:erp-session-expired";
const COOKIE_ONLY_AUTH_TOKEN_MARKER = "__cookie_session__";

// Quando a sessão expira de facto, a app tem várias chamadas em curso ao
// mesmo tempo (heartbeat, polling do badge AGT, auto-refresh do hub
// fiscal, sincronização de catálogo, etc.) — cada uma recebe o seu
// próprio 401 e, sem isto, cada uma disparava ERP_SESSION_EXPIRED_EVENT
// em separado. Os ouvintes (erpContext.js, Routes.jsx) reagem a cada
// disparo — o de Routes.jsx reinicia uma transição visual de 450ms a
// cada evento, o que com vários 401s a chegar em sequência dava o efeito
// de "vários refresh" antes do redirecionamento real para /login
// acontecer. Já não precisa de ser reposto a false: redirectToLogin() faz
// window.location.assign() — uma navegação completa que recarrega toda a
// página, o que já limpa este módulo (e todo o resto do estado JS) por
// si só.
let sessionExpiredEventDispatched = false;

function isOfflineSessionToken(token) {
  return String(token || "").trim() === "offline-session";
}

function normalizeAuthTokenValue(value) {
  const normalized = String(value || "")
    .replace(/^Bearer\s+/i, "")
    .trim();

  if (!normalized) {
    return "";
  }

  const invalidLiterals = ["undefined", "null", "nan", "false"];
  if (invalidLiterals.includes(normalized.toLowerCase())) {
    return "";
  }

  return normalized;
}

function resolvePersistedAuthToken() {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const sessionRaw = window.sessionStorage.getItem("angolabi.erp.authSession");
    if (sessionRaw) {
      const parsed = JSON.parse(sessionRaw);
      const token = normalizeAuthTokenValue(parsed?.token);
      if (token) return token;
    }
  } catch {
    // ignore
  }

  return "";
}

function persistCookieOnlyAuthMarker() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const sessionRaw = window.sessionStorage.getItem("angolabi.erp.authSession");
    if (!sessionRaw) {
      return;
    }

    const parsed = JSON.parse(sessionRaw);
    if (!parsed || typeof parsed !== "object") {
      return;
    }

    if (String(parsed.token || "").trim() === COOKIE_ONLY_AUTH_TOKEN_MARKER) {
      return;
    }

    parsed.token = COOKIE_ONLY_AUTH_TOKEN_MARKER;
    window.sessionStorage.setItem("angolabi.erp.authSession", JSON.stringify(parsed));
  } catch {
    // ignore marker persistence failures
  }
}

function resolveEffectiveAuthToken(candidateToken) {
  const direct = normalizeAuthTokenValue(candidateToken);
  if (direct) {
    return direct;
  }

  const persisted = resolvePersistedAuthToken();
  return normalizeAuthTokenValue(persisted);
}

function buildAuthorizationHeader(candidateToken) {
  const token = resolveEffectiveAuthToken(candidateToken);
  if (!token) {
    return null;
  }

  return `Bearer ${token}`;
}

if (IS_TAURI_DESKTOP && !ENV_API_BASE_URL) {
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const isNetwork = !status;
      const originalRequest = error?.config;

      if (!isNetwork || !originalRequest || originalRequest.__desktopRetried) {
        return Promise.reject(error);
      }

      const currentBase = String(originalRequest.baseURL || api.defaults.baseURL || "");
      const alternativeBase = DESKTOP_API_BASE_URLS.find((url) => url !== currentBase);
      if (!alternativeBase) {
        return Promise.reject(error);
      }

      originalRequest.__desktopRetried = true;
      originalRequest.baseURL = alternativeBase;
      api.defaults.baseURL = alternativeBase;
      return api.request(originalRequest);
    }
  );
}

// Intercepta respostas de sessão expirada para logout automático
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const responseStatus = Number(error?.response?.status || 0);
    const isSessionExpiredStatus = responseStatus === 401 || responseStatus === 419 || responseStatus === 440;

    if (isSessionExpiredStatus) {
      const requestUrl = String(error?.config?.url || "").toLowerCase();
      const requestMethod = String(error?.config?.method || "get").toLowerCase();
      const forceAutoAuth = Boolean(error?.config?.forceAutoAuth);
      const isAuthBootstrapRoute = requestUrl.includes("/login.php") || requestUrl.includes("/register.php");
      const isLogoutRoute = requestUrl.includes("/logout.php");
      const isSessionValidationRoute = requestUrl.includes("/session.php") || requestUrl.includes("/session_bootstrap.php");
      const isPublicBlockedPagesRoute = requestMethod === "get" && requestUrl.includes("/paginas_sistema.php") && !forceAutoAuth;
      const hasExplicitAuthorizationHeader = Boolean(String(
        error?.config?.headers?.Authorization || error?.config?.headers?.authorization || ""
      ).trim());
      const requestToken = String(error?.config?.headers?.Authorization || "")
        .replace(/^Bearer\s+/i, "")
        .trim();
      const persistedToken = resolvePersistedAuthToken();
      const hasAuthenticatedSession = Boolean(requestToken || persistedToken);
      const shouldIgnoreOffline401 = isOfflineSessionToken(requestToken) || isOfflineSessionToken(persistedToken);

      if (
        shouldIgnoreOffline401
        || isAuthBootstrapRoute
        || isLogoutRoute
        || isSessionValidationRoute
        || isPublicBlockedPagesRoute
        || !hasExplicitAuthorizationHeader
        || !hasAuthenticatedSession
      ) {
        return Promise.reject(error);
      }

      if (typeof window !== "undefined") {
        const currentPath = String(window.location?.pathname || "");
        if (currentPath === "/login") {
          return Promise.reject(error);
        }

        if (import.meta.env.DEV) {
          console.warn("[AUTH] resposta tratada como sessao expirada", {
            requestUrl: requestUrl || "",
            statusCode: responseStatus,
          });
        }

        // Só o primeiro 401/419/440 desta sessão dispara o evento global —
        // ver comentário junto de sessionExpiredEventDispatched acima.
        if (!sessionExpiredEventDispatched) {
          sessionExpiredEventDispatched = true;
          window.dispatchEvent(new CustomEvent(ERP_SESSION_EXPIRED_EVENT, {
            detail: {
              reason: "unauthorized",
              statusCode: responseStatus,
              requestUrl: requestUrl || "",
            },
          }));
        }
      }
    }
    return Promise.reject(error);
  }
);

const ENABLE_API_DEVTOOLS_LOG = Boolean(
  import.meta.env.DEV
  || String(import.meta.env.VITE_ENABLE_API_DEVTOOLS_LOG || "").toLowerCase() === "true"
);

const API_LOG_SENSITIVE_KEY_PATTERN = /authorization|token|password|senha|secret|chave|private|assinatura|signature|cpf|nif|pin/i;
let apiRequestSequence = 0;
const SESSION_REQUEST_CACHE_TTL_MS = 1500;

let inFlightSessionValidationRequest = null;
let inFlightSessionBootstrapRequest = null;
let cachedSessionValidationResponse = null;
let cachedSessionBootstrapResponse = null;

function safeParseJson(value) {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function maskSensitiveText(value) {
  const stringValue = String(value ?? "");
  if (!stringValue) {
    return stringValue;
  }

  if (stringValue.length <= 8) {
    return "***";
  }

  return `${stringValue.slice(0, 3)}***${stringValue.slice(-2)}`;
}

function sanitizeForDevtools(value, key = "", depth = 0) {
  if (depth > 4) {
    return "[depth-limit]";
  }

  const normalizedKey = String(key || "");
  if (API_LOG_SENSITIVE_KEY_PATTERN.test(normalizedKey)) {
    return maskSensitiveText(value);
  }

  const parsedValue = safeParseJson(value);

  if (parsedValue == null) {
    return parsedValue;
  }

  if (Array.isArray(parsedValue)) {
    return parsedValue.map((item) => sanitizeForDevtools(item, normalizedKey, depth + 1));
  }

  if (typeof parsedValue === "object") {
    return Object.entries(parsedValue).reduce((accumulator, [entryKey, entryValue]) => {
      accumulator[entryKey] = sanitizeForDevtools(entryValue, entryKey, depth + 1);
      return accumulator;
    }, {});
  }

  if (typeof parsedValue === "string" && parsedValue.length > 600) {
    return `${parsedValue.slice(0, 600)}...[truncated:${parsedValue.length}]`;
  }

  return parsedValue;
}

function resolveRequestUrl(config) {
  const requestBase = String(config?.baseURL || api.defaults.baseURL || "").trim();
  const requestPath = String(config?.url || "").trim();
  if (/^https?:\/\//i.test(requestPath)) {
    return requestPath;
  }

  if (!requestBase) {
    return requestPath;
  }

  if (!requestPath) {
    return requestBase;
  }

  const normalizedBase = requestBase.endsWith("/") ? requestBase.slice(0, -1) : requestBase;
  const normalizedPath = requestPath.startsWith("/") ? requestPath : `/${requestPath}`;
  return `${normalizedBase}${normalizedPath}`;
}

api.interceptors.request.use(
  (config) => {
    const requestUrl = String(config?.url || "").toLowerCase();
    const requestMethod = String(config?.method || "get").toLowerCase();
    const forceAutoAuth = Boolean(config?.forceAutoAuth);
    const skipAutoAuth = Boolean(config?.skipAutoAuth);
    const isAuthBootstrapRoute = requestUrl.includes("/login.php") || requestUrl.includes("/register.php");
    const isLogoutRoute = requestUrl.includes("/logout.php");
    const isPublicBlockedPagesRoute = requestMethod === "get" && requestUrl.includes("/paginas_sistema.php");
    const hasExplicitAuthHeader = Boolean(String(config?.headers?.Authorization || config?.headers?.authorization || "").trim());

    // paginas_sistema.php tem modo publico para leitura de páginas bloqueadas.
    // Só envia Authorization quando a chamada marcar forceAutoAuth=true (caso admin).
    if (isPublicBlockedPagesRoute && !forceAutoAuth) {
      if (config?.headers) {
        delete config.headers.Authorization;
        delete config.headers.authorization;
      }
    }

    if (!skipAutoAuth && !isAuthBootstrapRoute && !isLogoutRoute && !(isPublicBlockedPagesRoute && !forceAutoAuth)) {
      const currentAuthHeader = String(config?.headers?.Authorization || config?.headers?.authorization || "").trim();
      const effectiveAuthHeader = buildAuthorizationHeader(currentAuthHeader);
      if (effectiveAuthHeader) {
        if (!config.headers) {
          config.headers = {};
        }
        config.headers.Authorization = effectiveAuthHeader;
      } else if (config?.headers) {
        // Evita enviar "Bearer " vazio, para permitir fallback por cookie HttpOnly.
        delete config.headers.Authorization;
        delete config.headers.authorization;
      }
    }

    if (ENABLE_API_DEVTOOLS_LOG) {
      apiRequestSequence += 1;
      const requestId = apiRequestSequence;
      const startedAt = Date.now();

      config.__devtoolsTrace = {
        requestId,
        startedAt,
      };

      console.debug(`[API][REQ ${requestId}] ${String(config?.method || "GET").toUpperCase()} ${resolveRequestUrl(config)}`, {
        params: sanitizeForDevtools(config?.params),
        data: sanitizeForDevtools(config?.data),
        headers: sanitizeForDevtools(config?.headers),
      });
    }

    return config;
  },
  (error) => {
    if (ENABLE_API_DEVTOOLS_LOG) {
      console.debug("[API][REQ-ERROR]", {
        message: String(error?.message || "Request setup error"),
        config: sanitizeForDevtools(error?.config || null),
      });
    }

    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (ENABLE_API_DEVTOOLS_LOG) {
      const requestId = response?.config?.__devtoolsTrace?.requestId ?? "-";
      const startedAt = Number(response?.config?.__devtoolsTrace?.startedAt || 0);
      const elapsed = startedAt > 0 ? (Date.now() - startedAt) : null;

      console.debug(`[API][RES ${requestId}] ${response?.status} ${resolveRequestUrl(response?.config)}`, {
        elapsedMs: elapsed,
        data: sanitizeForDevtools(response?.data),
      });
    }

    return response;
  },
  (error) => {
    if (ENABLE_API_DEVTOOLS_LOG) {
      const requestId = error?.config?.__devtoolsTrace?.requestId ?? "-";
      const startedAt = Number(error?.config?.__devtoolsTrace?.startedAt || 0);
      const elapsed = startedAt > 0 ? (Date.now() - startedAt) : null;

      console.debug(`[API][ERR ${requestId}] ${error?.response?.status || "NO_STATUS"} ${resolveRequestUrl(error?.config)}`, {
        elapsedMs: elapsed,
        message: String(error?.message || "Request failed"),
        response: sanitizeForDevtools(error?.response?.data),
      });
    }

    return Promise.reject(error);
  }
);

const ENABLE_AGT_DEBUG = Boolean(import.meta.env.DEV || String(import.meta.env.VITE_ENABLE_AGT_DEBUG || "").toLowerCase() === "true");

function logAgtDebug(label, payload) {
  if (!ENABLE_AGT_DEBUG) {
    return;
  }

  console.debug(`[AGT] ${label}`, payload);
}

function resolveApiError(error, fallbackMessage) {
  const responseMessage = String(error?.response?.data?.message || "").trim();
  const transportMessage = String(error?.message || "").trim();
  // data.error cobre endpoints legados em inglês; data.erro cobre os que
  // usam envelope português {sucesso, erro} — ex.: movimentos_cofre.php.
  const payloadText = String(error?.response?.data?.error || error?.response?.data?.erro || "").trim();
  const connectionText = `${responseMessage} ${transportMessage} ${payloadText}`.toLowerCase();
  const status = Number(error?.response?.status || 0);
  const isTechnicalSqlError = /sqlstate\[|invalid parameter number|integrity constraint|constraint failed|hy093/i.test(connectionText);

  if (isTechnicalSqlError) {
    if (IS_TAURI_DESKTOP) {
      const isLocalApiConfigured = /localhost|127\.0\.0\.1/i.test(String(api.defaults?.baseURL || ""));
      if (isLocalApiConfigured) {
        return "Falha na base de dados local. Verifique se o MySQL do XAMPP está ativo e tente novamente.";
      }

      return "Falha na base de dados do servidor. Tente novamente em instantes.";
    }

    return "Falha temporaria na base de dados. Tente novamente em instantes.";
  }

  const hasConnectionFailure = (
    !error?.response
    || status <= 0
    || /sqlstate|\[2002\]|hy000|falha na liga[cç][aã]o [àa] base de dados|nenhuma liga[cç][aã]o p[oô]de ser feita|refused|econnrefused|network error|failed to fetch|connection/i.test(connectionText)
  );

  // Se a API respondeu com 4xx/5xx, prioriza a mensagem do backend.
  // payloadText (data.error) cobre endpoints legados com envelope
  // {sucesso, erro} em vez de {success, message} — ex.: movimentos_cofre.php.
  if (status >= 400) {
    if (responseMessage) return responseMessage;
    if (payloadText) return payloadText;
    if (transportMessage) return transportMessage;
    return fallbackMessage;
  }

  if (hasConnectionFailure) {
    if (IS_TAURI_DESKTOP) {
      const isLocalApiConfigured = /localhost|127\.0\.0\.1/i.test(String(api.defaults?.baseURL || ""));
      if (isLocalApiConfigured) {
        return "Sem ligação ao servidor local. Verifique se o XAMPP (Apache/MySQL) está ativo e se a API local está acessível.";
      }

      return "Nao foi possivel ligar ao servidor. Verifique a internet ou a configuracao do servidor e tente novamente.";
    }

    return "Sem conexão à internet. Conecte-se à internet para continuar.";
  }

  if (responseMessage) return responseMessage;
  if (transportMessage) return transportMessage;
  return fallbackMessage;
}

function buildApiError(error, fallbackMessage) {
  const nextError = new Error(resolveApiError(error, fallbackMessage));
  nextError.response = error?.response;
  nextError.code = error?.code;
  nextError.apiCode = error?.response?.data?.code || null;
  nextError.apiData = error?.response?.data?.data || null;
  nextError.apiPayload = error?.response?.data || null;
  nextError.cause = error;
  return nextError;
}

function ensureSuccessfulApiPayload(response, fallbackMessage) {
  const payload = response?.data;
  const hasSuccessFlag = payload && typeof payload === "object" && Object.prototype.hasOwnProperty.call(payload, "success");

  if (hasSuccessFlag && payload?.success === false) {
    const explicitMessage = String(payload?.message || "").trim();
    const logicalError = new Error(explicitMessage || fallbackMessage);
    logicalError.response = {
      ...(response || {}),
      data: payload,
      status: response?.status,
    };
    logicalError.apiCode = payload?.code || null;
    logicalError.apiData = payload?.data || null;
    logicalError.apiPayload = payload;
    throw logicalError;
  }

  return payload?.data ?? null;
}

function formatStructuredApiErrors(payload) {
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const values = Array.isArray(payload.errors) ? payload.errors : [];
  return values
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

function stringifyApiPayload(payload) {
  try {
    return JSON.stringify(payload ?? null, null, 2);
  } catch {
    return String(payload ?? "");
  }
}

const BLOCKED_PAGES_SNAPSHOT_KEY = "angolabi.erp.blockedPagesSnapshot";
const BLOCKED_PAGES_SNAPSHOT_VERSION = 2;
const PERMISSION_CATALOG_SNAPSHOT_KEY = "angolabi.erp.permissionCatalogSnapshot";
const PERMISSION_CATALOG_SNAPSHOT_VERSION = 1;
let blockedPagesHydrationPromise = null;

function readPersistedAuthSessionRaw() {
  if (typeof window === "undefined") {
    return null;
  }

  const sources = [window.sessionStorage, window.localStorage];
  for (const storage of sources) {
    try {
      const raw = storage?.getItem("angolabi.erp.authSession");
      if (raw) {
        return raw;
      }
    } catch {
      // ignore storage read failures
    }
  }

  return null;
}

function readPersistedBlockedPagesScopeMeta() {
  if (typeof window === "undefined") {
    return {
      userUuid: "anonymous",
      companyUuid: "global",
      storeUuid: "all",
    };
  }

  let parsedSession = null;
  try {
    const raw = readPersistedAuthSessionRaw();
    parsedSession = raw ? JSON.parse(raw) : null;
  } catch {
    parsedSession = null;
  }

  const currentUser = parsedSession?.currentUser && typeof parsedSession.currentUser === "object"
    ? parsedSession.currentUser
    : {};

  const userUuid = String(currentUser?.uuid || "").trim() || "anonymous";
  const companyUuid = String(currentUser?.companyUuid || currentUser?.company_uuid || window.localStorage.getItem("angolabi.erp.companyId") || "").trim() || "global";
  const storeUuid = String(currentUser?.storeUuid || currentUser?.store_uuid || window.localStorage.getItem("angolabi.erp.storeId") || "").trim() || "all";

  return {
    userUuid,
    companyUuid,
    storeUuid,
  };
}

function buildBlockedPagesScopeKey(scopeMeta = {}) {
  const userUuid = String(scopeMeta?.userUuid || "anonymous").trim() || "anonymous";
  const companyUuid = String(scopeMeta?.companyUuid || "global").trim() || "global";
  const storeUuid = String(scopeMeta?.storeUuid || "all").trim() || "all";
  return `${userUuid}::${companyUuid}::${storeUuid}`;
}

export function getCurrentBlockedPagesScopeKey() {
  return buildBlockedPagesScopeKey(readPersistedBlockedPagesScopeMeta());
}

async function getOfflineStorageInvoke() {
  if (!IS_TAURI_DESKTOP) {
    return null;
  }

  try {
    const core = await import("@tauri-apps/api/core");
    return core?.invoke || null;
  } catch {
    return null;
  }
}

async function readDesktopBlockedPagesSnapshotRaw() {
  const invoke = await getOfflineStorageInvoke();
  if (!invoke) {
    return null;
  }

  try {
    const raw = await invoke("offline_storage_get_json", { key: BLOCKED_PAGES_SNAPSHOT_KEY });
    return typeof raw === "string" && raw.trim() ? raw : null;
  } catch {
    return null;
  }
}

async function writeDesktopBlockedPagesSnapshotRaw(rawValue) {
  const invoke = await getOfflineStorageInvoke();
  if (!invoke || !rawValue) {
    return;
  }

  try {
    await invoke("offline_storage_set_json", { key: BLOCKED_PAGES_SNAPSHOT_KEY, value: rawValue });
  } catch {
    // ignore desktop persistence errors
  }
}

async function removeDesktopBlockedPagesSnapshotRaw() {
  const invoke = await getOfflineStorageInvoke();
  if (!invoke) {
    return;
  }

  try {
    await invoke("offline_storage_remove", { key: BLOCKED_PAGES_SNAPSHOT_KEY });
  } catch {
    // ignore desktop removal errors
  }
}

function normalizeBlockedPages(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter((item) => item && typeof item === "object");
}

function normalizeBlockedPagesSnapshotEntries(entries) {
  if (!entries || typeof entries !== "object") {
    return {};
  }

  return Object.entries(entries).reduce((accumulator, [scopeKey, value]) => {
    if (!scopeKey || !value || typeof value !== "object") {
      return accumulator;
    }

    accumulator[scopeKey] = {
      cachedAt: String(value?.cachedAt || "").trim() || new Date().toISOString(),
      scope: {
        userUuid: String(value?.scope?.userUuid || "anonymous").trim() || "anonymous",
        companyUuid: String(value?.scope?.companyUuid || "global").trim() || "global",
        storeUuid: String(value?.scope?.storeUuid || "all").trim() || "all",
      },
      items: normalizeBlockedPages(value?.items),
    };
    return accumulator;
  }, {});
}

function readBlockedPagesSnapshotCollection() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(BLOCKED_PAGES_SNAPSHOT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const version = Number(parsed?.version || 0);

    if (version === BLOCKED_PAGES_SNAPSHOT_VERSION) {
      return {
        version,
        entries: normalizeBlockedPagesSnapshotEntries(parsed?.entries),
      };
    }

    if (version === 1 && Array.isArray(parsed?.items)) {
      const scope = readPersistedBlockedPagesScopeMeta();
      const scopeKey = buildBlockedPagesScopeKey(scope);
      return {
        version: BLOCKED_PAGES_SNAPSHOT_VERSION,
        entries: {
          [scopeKey]: {
            cachedAt: String(parsed?.cachedAt || "").trim() || new Date().toISOString(),
            scope,
            items: normalizeBlockedPages(parsed.items),
          },
        },
      };
    }

    return null;
  } catch {
    return null;
  }
}

function readBlockedPagesSnapshot() {
  const collection = readBlockedPagesSnapshotCollection();
  if (!collection) {
    return null;
  }

  const scopeKey = getCurrentBlockedPagesScopeKey();
  const entry = collection.entries?.[scopeKey];
  if (!entry) {
    return null;
  }

  return {
    items: normalizeBlockedPages(entry?.items),
    trusted: true,
    scopeKey,
  };
}

export async function ensureBlockedPagesSnapshotHydrated() {
  if (typeof window === "undefined" || !IS_TAURI_DESKTOP) {
    return;
  }

  if (readBlockedPagesSnapshotCollection()) {
    return;
  }

  if (blockedPagesHydrationPromise) {
    await blockedPagesHydrationPromise;
    return;
  }

  blockedPagesHydrationPromise = (async () => {
    const raw = await readDesktopBlockedPagesSnapshotRaw();
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const version = Number(parsed?.version || 0);
      if (version === BLOCKED_PAGES_SNAPSHOT_VERSION) {
        const serialized = JSON.stringify({
          version: BLOCKED_PAGES_SNAPSHOT_VERSION,
          entries: normalizeBlockedPagesSnapshotEntries(parsed?.entries),
        });

        window.localStorage.setItem(BLOCKED_PAGES_SNAPSHOT_KEY, serialized);
        return;
      }

      if (version === 1 && Array.isArray(parsed?.items)) {
        const scope = readPersistedBlockedPagesScopeMeta();
        const scopeKey = buildBlockedPagesScopeKey(scope);
        const serialized = JSON.stringify({
          version: BLOCKED_PAGES_SNAPSHOT_VERSION,
          entries: {
            [scopeKey]: {
              cachedAt: String(parsed?.cachedAt || "").trim() || new Date().toISOString(),
              scope,
              items: normalizeBlockedPages(parsed.items),
            },
          },
        });

        window.localStorage.setItem(BLOCKED_PAGES_SNAPSHOT_KEY, serialized);
      }
    } catch {
      // ignore invalid desktop snapshot payload
    }
  })();

  try {
    await blockedPagesHydrationPromise;
  } finally {
    blockedPagesHydrationPromise = null;
  }
}

function writeBlockedPagesSnapshot(items) {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedItems = normalizeBlockedPages(items);
  const scope = readPersistedBlockedPagesScopeMeta();
  const scopeKey = buildBlockedPagesScopeKey(scope);
  const currentCollection = readBlockedPagesSnapshotCollection();
  const nextEntries = {
    ...(currentCollection?.entries || {}),
    [scopeKey]: {
      cachedAt: new Date().toISOString(),
      scope,
      items: normalizedItems,
    },
  };

  try {
    const serialized = JSON.stringify({
      version: BLOCKED_PAGES_SNAPSHOT_VERSION,
      entries: nextEntries,
    });

    window.localStorage.setItem(BLOCKED_PAGES_SNAPSHOT_KEY, serialized);
    void writeDesktopBlockedPagesSnapshotRaw(serialized);
  } catch {
    // ignore localStorage errors
  }
}

export function readCachedBlockedPages() {
  const snapshot = readBlockedPagesSnapshot();
  return Array.isArray(snapshot?.items) ? snapshot.items : [];
}

export function hasCachedBlockedPagesSnapshot() {
  const snapshot = readBlockedPagesSnapshot();
  return Boolean(snapshot?.trusted && Array.isArray(snapshot?.items));
}

export function clearCachedBlockedPagesSnapshot() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(BLOCKED_PAGES_SNAPSHOT_KEY);
    void removeDesktopBlockedPagesSnapshotRaw();
  } catch {
    // ignore localStorage errors
  }
}

function normalizePermissionCatalogItems(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      codigo_permissao: String(item?.codigo_permissao || item?.id || "").trim(),
      modulo_chave: String(item?.modulo_chave || item?.resource || "").trim().toLowerCase(),
      acao_chave: String(item?.acao_chave || item?.action || "").trim().toLowerCase(),
      nome: String(item?.nome || item?.label || "").trim(),
      descricao: String(item?.descricao || item?.desc || "").trim(),
    }))
    .filter((item) => item.codigo_permissao !== "");
}

function writePermissionCatalogSnapshot(items) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(PERMISSION_CATALOG_SNAPSHOT_KEY, JSON.stringify({
      version: PERMISSION_CATALOG_SNAPSHOT_VERSION,
      cachedAt: new Date().toISOString(),
      items: normalizePermissionCatalogItems(items),
    }));
  } catch {
    // ignore localStorage errors
  }
}

function readPermissionCatalogSnapshot() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(PERMISSION_CATALOG_SNAPSHOT_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (Number(parsed?.version || 0) !== PERMISSION_CATALOG_SNAPSHOT_VERSION) {
      return null;
    }

    return {
      items: normalizePermissionCatalogItems(parsed?.items),
      trusted: true,
    };
  } catch {
    return null;
  }
}

export function readCachedPermissionCatalog() {
  const snapshot = readPermissionCatalogSnapshot();
  return Array.isArray(snapshot?.items) ? snapshot.items : [];
}

export async function apiLogin(credentials) {
  try {
    const response = await api.post("/login.php", credentials, {
      withCredentials: true,
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, "Não foi possível autenticar.");
  }
}

export async function apiSetLoginPin(token, { pin, deviceId, deviceName } = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post("/pin.php", {
      action: "definir",
      pin,
      dispositivo_id: deviceId,
      dispositivo_nome: deviceName,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, "Não foi possível configurar o PIN.");
  }
}

export async function apiLoginWithPin({ identifier, deviceId, pin } = {}) {
  try {
    const response = await api.post("/pin.php", {
      action: "login",
      identificador: identifier,
      dispositivo_id: deviceId,
      pin,
    }, {
      withCredentials: true,
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, "Não foi possível autenticar com o PIN.");
  }
}

export async function apiRemoveLoginPin(token, { deviceId } = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post("/pin.php", {
      action: "remover",
      dispositivo_id: deviceId,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, "Não foi possível remover o PIN.");
  }
}

export async function apiRegisterUser(payload) {
  try {
    const response = await api.post("/register.php", payload);
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel criar a conta.");
  }
}

export async function apiRequestCredentialsOtp(payload) {
  try {
    const response = await api.post("/request_credentials_otp.php", payload, {
      withCredentials: true,
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel enviar o OTP para confirmacao de email.");
  }
}

export async function apiVerifyCredentialsOtp(payload) {
  try {
    const response = await api.post("/verify_credentials_otp.php", payload, {
      withCredentials: true,
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel verificar o codigo OTP.");
  }
}

export async function apiResendCredentialsOtp(payload) {
  try {
    const response = await api.post("/resend_credentials_otp.php", payload, {
      withCredentials: true,
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel reenviar o codigo OTP.");
  }
}

export async function apiCreateCompanySetup(token, payload) {
  try {
    const response = await api.post("/company_setup.php", payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel concluir a configuracao inicial.");
  }
}

export async function apiGetSubscriptionStatus(token) {
  try {
    const response = await api.get("/subscriptions.php", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel carregar o estado da assinatura.");
  }
}

export async function apiRenewSubscription(token, payload) {
  try {
    const response = await api.post("/subscriptions.php", payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel renovar a assinatura.");
  }
}

export async function apiCreateSubscriptionRequest(token, payload) {
  try {
    const response = await api.post('/solicitacoes_assinatura.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel criar a solicitacao de assinatura.');
  }
}

export async function apiListSubscriptionRequests(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/solicitacoes_assinatura.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        estado: params?.estado || undefined,
        limit: params?.limit || 50,
        offset: params?.offset || 0,
      },
    });

    return response?.data?.data || { solicitacoes: [], contagem: {} };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel listar as solicitacoes de assinatura.');
  }
}

export async function apiReviewSubscriptionRequest(token, requestUuid, payload) {
  try {
    const response = await api.patch(`/solicitacoes_assinatura.php?uuid=${encodeURIComponent(requestUuid)}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar o estado da solicitacao.');
  }
}

export async function apiListCredentialAccessRequests(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/solicitacoes_credenciais.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        estado: params?.estado || undefined,
        limit: params?.limit || 100,
        offset: params?.offset || 0,
      },
    });

    return response?.data?.data || { solicitacoes: [], contagem: {} };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel listar as solicitacoes de credenciais.');
  }
}

export async function apiReviewCredentialAccessRequest(token, requestUuid, payload = {}) {
  try {
    const response = await api.patch(`/solicitacoes_credenciais.php?uuid=${encodeURIComponent(requestUuid)}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel processar a solicitacao de credenciais.');
  }
}

export async function apiGetSubscriptionLicenses(token, options = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const companyUuid = String(options?.companyUuid || options?.uuid_empresa || '').trim();
    const response = await api.get('/assinaturas_empresa.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        uuid_empresa: companyUuid || undefined,
      },
    });

    return response?.data?.data || { ativa: null, historico: [], auditoria: [] };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as licencas de assinatura.');
  }
}

export async function apiActivateSubscriptionLicense(token, payload) {
  try {
    const response = await api.post('/assinaturas_empresa.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel ativar a licenca de assinatura.');
  }
}

export async function apiUpdateSubscriptionPrice(token, payload) {
  try {
    const response = await api.patch('/assinaturas_empresa.php', {
      acao: 'atualizar_preco',
      ...payload,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar o preco da assinatura.');
  }
}

export async function apiUpdateSubscriptionValidity(token, payload) {
  try {
    const response = await api.patch('/assinaturas_empresa.php', {
      acao: 'atualizar_validade',
      ...payload,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a validade da assinatura.');
  }
}

export async function apiSetPaymentCommitment(token, payload) {
  try {
    const response = await api.patch('/assinaturas_empresa.php', {
      acao: 'definir_compromisso_pagamento',
      ...payload,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel definir o compromisso de pagamento.');
  }
}

export async function apiRestrictCompanyAccess(token, payload) {
  try {
    const response = await api.patch('/assinaturas_empresa.php', {
      acao: 'restringir_acesso',
      ...payload,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel restringir o acesso da empresa.');
  }
}

export async function apiConfirmCompanyPayment(token, payload) {
  try {
    const response = await api.patch('/assinaturas_empresa.php', {
      acao: 'confirmar_pagamento',
      ...payload,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel confirmar o pagamento.');
  }
}

export async function apiCancelCompanySubscription(token, payload = {}) {
  try {
    const response = await api.patch('/assinaturas_empresa.php', {
      acao: 'cancelar',
      ...payload,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel cancelar a assinatura.');
  }
}

export async function apiListSubscriptionDevices(token, options = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const companyUuid = String(options?.companyUuid || options?.uuid_empresa || "").trim();

    const response = await api.get('/assinaturas_empresa.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        action: 'dispositivos',
        uuid_empresa: companyUuid || undefined,
      },
    });

    return response?.data?.data || { items: [], total: 0 };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel listar os dispositivos da assinatura.');
  }
}

export async function apiUpdateSubscriptionLimits(token, payload = {}) {
  try {
    const numeroLojaRaw = payload?.numero_loja ?? payload?.max_lojas;
    const numeroDispositivosRaw = payload?.numero_dispositivos ?? payload?.max_dispositivos;

    const response = await api.patch('/assinaturas_empresa.php', {
      acao: 'atualizar_limites',
      uuid_empresa: payload?.companyUuid || payload?.uuid_empresa || undefined,
      // 0 é um valor válido (bloqueio total quando ambos os campos são 0) — não
      // pode cair para `undefined` como um valor "falsy" qualquer.
      numero_loja: numeroLojaRaw !== undefined && numeroLojaRaw !== null && numeroLojaRaw !== "" ? Number(numeroLojaRaw) : undefined,
      numero_dispositivos: numeroDispositivosRaw !== undefined && numeroDispositivosRaw !== null && numeroDispositivosRaw !== "" ? Number(numeroDispositivosRaw) : undefined,
      motivo: payload?.motivo || undefined,
      // Contexto de preço para o registo de auditoria (assinaturas_empresa_auditoria).
      plano_codigo: payload?.plano_codigo || undefined,
      preco_unitario_loja_aoa: payload?.preco_unitario_loja_aoa ?? undefined,
      preco_unitario_dispositivo_aoa: payload?.preco_unitario_dispositivo_aoa ?? undefined,
      valor_estimado_anterior_aoa: payload?.valor_estimado_anterior_aoa ?? undefined,
      valor_estimado_novo_aoa: payload?.valor_estimado_novo_aoa ?? undefined,
      valor_estimado_acrescimo_aoa: payload?.valor_estimado_acrescimo_aoa ?? undefined,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar os limites da assinatura.');
  }
}

export async function apiRemoveSubscriptionDevice(token, payload = {}) {
  try {
    const response = await api.patch('/assinaturas_empresa.php', {
      acao: 'remover_dispositivo',
      uuid_empresa: payload?.companyUuid || payload?.uuid_empresa || undefined,
      uuid_dispositivo: payload?.deviceUuid || payload?.uuid_dispositivo || undefined,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel remover o dispositivo.');
  }
}

// Remove em lote dispositivos inativos há N dias (liberta lugares do limite
// sem remover um a um). admin_super apenas.
export async function apiPurgeStaleDevices(token, payload = {}) {
  try {
    const response = await api.patch('/assinaturas_empresa.php', {
      acao: 'purge_dispositivos_inativos',
      uuid_empresa: payload?.companyUuid || payload?.uuid_empresa || undefined,
      dias: payload?.dias || 30,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível limpar os dispositivos inativos.');
  }
}

export async function apiRegisterSubscriptionDevice(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const companyUuid = String(payload?.companyUuid || payload?.uuid_empresa || '').trim();

    const response = await api.post(
      '/assinaturas_empresa.php',
      {
        acao: 'registar_dispositivo',
        uuid_empresa: companyUuid || undefined,
        deviceName: String(payload?.deviceName || payload?.nome_dispositivo || '').trim(),
        deviceType: String(payload?.deviceType || payload?.tipo_dispositivo || 'desktop').trim(),
        platform: String(payload?.platform || 'web').trim(),
        fingerprint: String(payload?.fingerprint || payload?.identificador_unico || '').trim(),
        storeUuid: String(payload?.storeUuid || payload?.uuid_loja || '').trim() || undefined,
      },
      {
        withCredentials: true,
        ...(authorization ? { headers: { Authorization: authorization } } : {}),
      }
    );

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registar o dispositivo.');
  }
}

export async function apiTestSubscriptionNotification(token, payload = {}) {
  try {
    const response = await api.post('/assinaturas_notificacao_teste.php', {
      tipo: payload?.tipo || payload?.acao || 'ativacao',
      uuid_empresa: payload?.companyUuid || payload?.uuid_empresa || undefined,
      plano_codigo: payload?.plano_codigo || payload?.plano || undefined,
      validade_inicio: payload?.validade_inicio || undefined,
      validade_fim: payload?.validade_fim || undefined,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel executar o teste de notificacao de subscricao.');
  }
}

export async function apiGetBlockedPages(options = {}) {
  const includeMeta = Boolean(options?.includeMeta);

  await ensureBlockedPagesSnapshotHydrated();

  try {
    // Esta chamada deve usar o modo público do endpoint para obter apenas
    // páginas bloqueadas, independentemente do papel autenticado.
    const requestConfig = {
      skipAutoAuth: true,
      forceAutoAuth: false,
    };

    const response = await api.get("/paginas_sistema.php", requestConfig);
    const items = normalizeBlockedPages(response?.data?.data?.paginas);
    writeBlockedPagesSnapshot(items);

    if (includeMeta) {
      return {
        items,
        trusted: true,
      };
    }

    return items;
  } catch {
    const fallbackItems = readCachedBlockedPages();
    const trusted = hasCachedBlockedPagesSnapshot();

    if (includeMeta) {
      return {
        items: fallbackItems,
        trusted,
      };
    }

    return fallbackItems;
  }
}

export async function apiGetAllPages(token) {
  try {
    const response = await api.get("/paginas_sistema.php", {
      forceAutoAuth: true,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.paginas || [];
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel carregar a lista de paginas do sistema.");
  }
}

export async function apiUpdatePageStatus(token, rota, data) {
  try {
    const response = await api.put("/paginas_sistema.php", { rota, ...data }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel atualizar o estado da pagina.");
  }
}

export async function apiListSystemCatalogTables(token) {
  try {
    const response = await api.get("/catalogos_sistema.php", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.catalogos || [];
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel carregar a lista de catalogos de sistema.");
  }
}

export async function apiListSystemCatalogItems(token, tabela, params = {}) {
  try {
    const response = await api.get("/catalogos_sistema.php", {
      headers: { Authorization: `Bearer ${token}` },
      params: { tabela, ...params },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel carregar o catalogo selecionado.");
  }
}

export async function apiCreateSystemCatalogItem(token, tabela, payload) {
  try {
    const response = await api.post(`/catalogos_sistema.php?tabela=${encodeURIComponent(tabela)}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel criar o registo.");
  }
}

export async function apiUpdateSystemCatalogItem(token, tabela, id, payload) {
  try {
    const response = await api.patch(`/catalogos_sistema.php?tabela=${encodeURIComponent(tabela)}`, { id, ...payload }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel atualizar o registo.");
  }
}

export async function apiDeleteSystemCatalogItem(token, tabela, id) {
  try {
    const response = await api.delete(`/catalogos_sistema.php?tabela=${encodeURIComponent(tabela)}`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { id },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, "Nao foi possivel eliminar o registo.");
  }
}

export async function apiValidateSession(token) {
  const authHeader = String(token || "").trim()
    ? { Authorization: `Bearer ${String(token).trim()}` }
    : {};
  const requestKey = String(authHeader?.Authorization || "cookie").trim().toLowerCase() || "cookie";
  const now = Date.now();

  if (
    cachedSessionValidationResponse
    && cachedSessionValidationResponse.key === requestKey
    && (now - cachedSessionValidationResponse.at) <= SESSION_REQUEST_CACHE_TTL_MS
  ) {
    return cachedSessionValidationResponse.data;
  }

  if (inFlightSessionValidationRequest?.key === requestKey && inFlightSessionValidationRequest?.promise) {
    return inFlightSessionValidationRequest.promise;
  }

  const requestPromise = (async () => {
  try {
    const response = await api.get("/session.php", {
      withCredentials: true,
      headers: {
        ...authHeader,
      },
    });

    const responseData = response?.data?.data;
    cachedSessionValidationResponse = {
      key: requestKey,
      at: Date.now(),
      data: responseData,
    };

    return responseData;
  } catch (error) {
    const statusCode = Number(error?.response?.status || 0);
    const hasExplicitAuthHeader = Boolean(String(authHeader?.Authorization || "").trim());

    // Em ambientes com cookie HttpOnly valido e token em memoria obsoleto,
    // tenta uma vez sem Authorization para evitar expiração falsa de sessão.
    if (statusCode === 401 && hasExplicitAuthHeader) {
      try {
        const fallbackResponse = await api.get("/session.php", {
          withCredentials: true,
          skipAutoAuth: true,
          headers: {},
        });

        const fallbackData = fallbackResponse?.data?.data;
        persistCookieOnlyAuthMarker();
        cachedSessionValidationResponse = {
          key: requestKey,
          at: Date.now(),
          data: fallbackData,
        };
        return fallbackData;
      } catch {
        // se falhar, segue para erro padrao abaixo
      }
    }

    throw buildApiError(error, "Sessão inválida ou expirada.");
  } finally {
    if (inFlightSessionValidationRequest?.key === requestKey) {
      inFlightSessionValidationRequest = null;
    }
  }
  })();

  inFlightSessionValidationRequest = {
    key: requestKey,
    promise: requestPromise,
  };

  return requestPromise;
}

export async function apiGetSessionBootstrap(token) {
  const authHeader = String(token || "").trim()
    ? { Authorization: `Bearer ${String(token).trim()}` }
    : {};
  const requestKey = String(authHeader?.Authorization || "cookie").trim().toLowerCase() || "cookie";
  const now = Date.now();

  if (
    cachedSessionBootstrapResponse
    && cachedSessionBootstrapResponse.key === requestKey
    && (now - cachedSessionBootstrapResponse.at) <= SESSION_REQUEST_CACHE_TTL_MS
  ) {
    return cachedSessionBootstrapResponse.data;
  }

  if (inFlightSessionBootstrapRequest?.key === requestKey && inFlightSessionBootstrapRequest?.promise) {
    return inFlightSessionBootstrapRequest.promise;
  }

  const requestPromise = (async () => {
  try {
    const response = await api.get('/session_bootstrap.php', {
      withCredentials: true,
      headers: {
        ...authHeader,
      },
    });

    const responseData = response?.data?.data || null;
    cachedSessionBootstrapResponse = {
      key: requestKey,
      at: Date.now(),
      data: responseData,
    };

    return responseData;
  } catch (error) {
    const statusCode = Number(error?.response?.status || 0);
    const hasExplicitAuthHeader = Boolean(String(authHeader?.Authorization || "").trim());

    // Retry cookie-only para evitar logout indevido quando o token em memoria
    // ficou obsoleto, mas o cookie HttpOnly ainda está valido.
    if (statusCode === 401 && hasExplicitAuthHeader) {
      try {
        const fallbackResponse = await api.get('/session_bootstrap.php', {
          withCredentials: true,
          skipAutoAuth: true,
          headers: {},
        });

        const fallbackData = fallbackResponse?.data?.data || null;
        persistCookieOnlyAuthMarker();
        cachedSessionBootstrapResponse = {
          key: requestKey,
          at: Date.now(),
          data: fallbackData,
        };
        return fallbackData;
      } catch {
        // se falhar, segue para erro padrao abaixo
      }
    }

    throw buildApiError(error, 'Nao foi possivel carregar o bootstrap da sessao.');
  } finally {
    if (inFlightSessionBootstrapRequest?.key === requestKey) {
      inFlightSessionBootstrapRequest = null;
    }
  }
  })();

  inFlightSessionBootstrapRequest = {
    key: requestKey,
    promise: requestPromise,
  };

  return requestPromise;
}

export async function apiLogout(token) {
  try {
    await api.post(
      "/logout.php",
      {},
      {
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    return true;
  } catch {
    return false;
  }
}

export async function apiListEmpresas(token) {
  try {
    const authHeader = String(token || "").trim()
      ? { Authorization: `Bearer ${String(token).trim()}` }
      : {};

    const response = await api.get('/empresas.php', {
      headers: {
        ...authHeader,
      },
      withCredentials: true,
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as empresas.');
  }
}

export async function apiListLojas(token, params = {}) {
  try {
    const normalizedToken = String(token || "").trim();
    const authHeader = normalizedToken
      ? { Authorization: `Bearer ${normalizedToken}` }
      : {};
    const queryParams = {};

    if (String(params?.companyUuid || "").trim()) {
      queryParams.empresa_uuid = String(params.companyUuid).trim();
    }
    if (String(params?.companyCode || "").trim()) {
      queryParams.empresa_codigo = String(params.companyCode).trim();
    }
    if (String(params?.status || "").trim()) {
      queryParams.estado = String(params.status).trim();
    }

    const response = await api.get('/lojas.php', {
      headers: {
        ...authHeader,
      },
      withCredentials: true,
      params: queryParams,
    });
    return response?.data?.data;
  } catch (error) {
    const statusCode = Number(error?.response?.status || 0);
    const hasExplicitAuthHeader = Boolean(String(token || "").trim());

    if (statusCode === 401 && hasExplicitAuthHeader) {
      try {
        const fallbackParams = {};
        if (String(params?.companyUuid || "").trim()) {
          fallbackParams.empresa_uuid = String(params.companyUuid).trim();
        }
        if (String(params?.companyCode || "").trim()) {
          fallbackParams.empresa_codigo = String(params.companyCode).trim();
        }
        if (String(params?.status || "").trim()) {
          fallbackParams.estado = String(params.status).trim();
        }

        const fallbackResponse = await api.get('/lojas.php', {
          withCredentials: true,
          skipAutoAuth: true,
          headers: {},
          params: fallbackParams,
        });

        persistCookieOnlyAuthMarker();
        return fallbackResponse?.data?.data;
      } catch {
        // segue para erro padrao
      }
    }

    throw buildApiError(error, 'Nao foi possivel carregar as lojas.');
  }
}

export async function apiListStoreSectors(token) {
  try {
    const normalizedToken = String(token || "").trim();
    const authHeader = normalizedToken
      ? { Authorization: `Bearer ${normalizedToken}` }
      : {};

    const response = await api.get('/lojas.php', {
      headers: {
        ...authHeader,
      },
      withCredentials: true,
      params: {
        action: 'setores',
      },
    });
    return response?.data?.data;
  } catch (error) {
    const statusCode = Number(error?.response?.status || 0);
    const hasExplicitAuthHeader = Boolean(String(token || "").trim());

    if (statusCode === 401 && hasExplicitAuthHeader) {
      try {
        const fallbackResponse = await api.get('/lojas.php', {
          withCredentials: true,
          skipAutoAuth: true,
          headers: {},
          params: {
            action: 'setores',
          },
        });

        persistCookieOnlyAuthMarker();
        return fallbackResponse?.data?.data;
      } catch {
        // segue para erro padrao
      }
    }

    throw buildApiError(error, 'Nao foi possivel carregar os sectores de atuacao.');
  }
}

export async function apiGetLojaConfig(token, lojaUuid = null) {
  try {
    const params = { action: 'config_loja' };
    if (lojaUuid) params.uuid_lojas = lojaUuid;
    const response = await api.get('/lojas.php', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response?.data?.data || { mostrar_debug_payload: false };
  } catch {
    return { mostrar_debug_payload: false };
  }
}

export async function apiCreateLoja(token, payload = {}) {
  try {
    const response = await api.post('/lojas.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return ensureSuccessfulApiPayload(response, 'Nao foi possivel criar a loja.');
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel criar a loja.');
  }
}

export async function apiUpdateLoja(token, identifier, payload = {}) {
  try {
    const response = await api.patch(`/lojas.php?store_uuid=${encodeURIComponent(identifier)}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return ensureSuccessfulApiPayload(response, 'Nao foi possivel atualizar a loja.');
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a loja.');
  }
}

export async function apiCheckStoreInactivationImpact(token, identifier) {
  try {
    const response = await api.get('/lojas.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        action: 'impacto_inactivacao',
        store_uuid: identifier,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel verificar o impacto de inativar esta loja.');
  }
}

export async function apiInactivateLoja(token, identifier) {
  try {
    const response = await api.patch(`/lojas.php?store_uuid=${encodeURIComponent(identifier)}`, {
      acao: 'inativar',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return ensureSuccessfulApiPayload(response, 'Nao foi possivel inativar a loja.');
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel inativar a loja.');
  }
}

export async function apiReactivateLoja(token, identifier) {
  try {
    const response = await api.patch(`/lojas.php?store_uuid=${encodeURIComponent(identifier)}`, {
      acao: 'reativar',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return ensureSuccessfulApiPayload(response, 'Nao foi possivel reativar a loja.');
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel reativar a loja.');
  }
}

export async function apiSelectUserStore(token, payload) {
  try {
    const response = await api.post('/selecionar_loja.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a loja ativa.');
  }
}

export async function apiListUsers(token, { lojaUuid, empresaCodigo } = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const params = {};
    if (lojaUuid) params.loja_uuid = lojaUuid;
    else if (empresaCodigo) params.empresa_codigo = empresaCodigo;
    const response = await api.get('/utilizadores.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: Object.keys(params).length ? params : undefined,
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os utilizadores.');
  }
}

export async function apiListRoles(token) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/papeis.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os papeis.');
  }
}

export async function apiUpdateRolePermissions(token, roleIdentifier, permissionCodes = []) {
  try {
    const response = await api.patch('/papeis.php', {
      codigo: roleIdentifier,
      permissoes: Array.isArray(permissionCodes) ? permissionCodes : [],
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar as permissoes do papel.');
  }
}

export async function apiListPermissions(token) {
  try {
    const response = await api.get('/permissoes.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response?.data?.data;
    writePermissionCatalogSnapshot(data);
    return data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as permissoes.');
  }
}

export async function apiUpdateUser(token, identifier, payload = {}) {
  try {
    const response = await api.patch('/utilizadores.php', {
      uuid: identifier,
      ...payload,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar o utilizador.');
  }
}

export async function apiChangePassword(token, payload = {}) {
  try {
    const response = await api.patch('/alterar_senha.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a palavra-passe.');
  }
}

export async function apiUpdateCompanyProfile(token, payload = {}) {
  try {
    const response = await api.patch('/empresas.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar os dados da empresa.');
  }
}

export async function apiUpdateCompanySettingsProfile(token, payload = {}) {
  try {
    const response = await api.patch('/empresa_perfil.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar o perfil da empresa.');
  }
}

// Câmbio AOA/USD — ver CambioRepository::getEffectiveSettings(). Sem
// `params.lojaUuid`, resolve o âmbito GLOBAL (toda a empresa); com uma
// loja, resolve o override dessa loja quando existir, senão herda o
// global (ver escopoResolvido na resposta: 'loja' | 'global' | 'nenhum').
// `params.forceRefresh` ignora o cache e busca uma taxa nova já neste
// pedido (botão "Atualizar agora").
export async function apiGetCambio(token, params = {}) {
  try {
    const response = await api.get('/organizacao/cambio.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        loja_uuid: params?.lojaUuid || undefined,
        atualizar: params?.forceRefresh ? 1 : undefined,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as definições de câmbio.');
  }
}

// `payload.lojaUuid` grava um override específico dessa loja em vez de
// alterar o global — omitido/vazio actualiza o âmbito global.
export async function apiUpdateCambio(token, payload = {}) {
  try {
    const response = await api.put('/organizacao/cambio.php', {
      ...payload,
      loja_uuid: payload?.lojaUuid || undefined,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível atualizar as definições de câmbio.');
  }
}

// Endpoint público (sem sessão) — destino do QR code de verificação interna
// impresso num documento. Qualquer pessoa com o link deve conseguir chamar
// isto, por isso não leva Authorization.
export async function apiVerificarDocumento(uuidDocumento) {
  try {
    // withCredentials:false explicito — a instância partilhada 'api' usa
    // withCredentials:true por omissão (para os endpoints autenticados), mas
    // este pedido não envia nenhuma sessão/cookie. Sem esta substituição, o
    // navegador exige um cabeçalho Access-Control-Allow-Credentials:true na
    // resposta (que este endpoint público de propósito não envia), e o
    // pedido é bloqueado pelo CORS antes de chegar à resposta.
    const response = await api.get('/verificar_documento.php', {
      params: { uuid: String(uuidDocumento || '').trim() },
      withCredentials: false,
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel verificar o documento.');
  }
}

export async function apiCreateUser(token, payload = {}) {
  try {
    const response = await api.post('/utilizadores.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel criar o utilizador.');
  }
}

export async function apiListUnits(token) {
  try {
    const response = await api.get('/unidades.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as unidades.');
  }
}

export async function apiListTaxes(token) {
  try {
    const response = await api.get('/impostos.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os impostos.');
  }
}

export async function apiListExemptionReasons(token) {
  try {
    const response = await api.get('/motivos_isencao.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os motivos de isencao.');
  }
}

export async function apiListOperationTypes(token) {
  try {
    const response = await api.get('/tipo_operacoes.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os tipos de operacao.');
  }
}

export async function apiListCategories(token) {
  try {
    const response = await api.get('/categorias.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as categorias.');
  }
}

export async function apiListSegments(token) {
  try {
    const response = await api.get('/segmentos.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os segmentos.');
  }
}

export async function apiListTaxRegimes(token) {
  try {
    const response = await api.get('/regime_tributario.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os regimes tributarios.');
  }
}

export async function apiListTaxRegimesSelect(token) {
  try {
    const response = await api.get('/regime_tributario_select.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os regimes tributarios para o select.');
  }
}

export async function apiListEconomicActivityCodes(token, params = {}) {
  try {
    const response = await api.get('/cae.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        q: String(params?.q || params?.query || '').trim() || undefined,
        limit: Number(params?.limit || 50) || 50,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os codigos de atividade economica (CAE).');
  }
}

export async function apiListDocumentTypes(token) {
  try {
    const response = await api.get('/tipos_documentos.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os tipos de documentos.');
  }
}

export async function apiListPaymentMethods(token) {
  try {
    const response = await api.get('/metodos_pagamento.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os metodos de pagamento.');
  }
}

export async function apiListBanks(token) {
  try {
    const response = await api.get('/bancos.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar a lista de bancos.');
  }
}

export async function apiGetCompanyBankCoordinates(token, params = {}) {
  try {
    const response = await api.get('/empresas_contas_bancarias.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        payment_type: params?.paymentType || 'transfer',
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as coordenadas bancarias da empresa.');
  }
}

export async function apiCreateCompanyBankCoordinate(token, payload = {}, params = {}) {
  try {
    const response = await api.post('/empresas_contas_bancarias.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel criar a coordenada bancaria.');
  }
}

export async function apiUpdateCompanyBankCoordinate(token, payload = {}, params = {}) {
  try {
    const response = await api.patch('/empresas_contas_bancarias.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        account_uuid: params?.accountUuid,
        account_id: params?.accountId,
        iban: params?.iban,
        conta: params?.conta,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a coordenada bancaria.');
  }
}

export async function apiListSystemBackups(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/backups_sistema.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        uuid_loja: params?.storeUuid,
        estado: params?.estado,
        tipo_backup: params?.tipoBackup,
        scope_tipo: params?.scopeTipo,
        include_auto_config: params?.includeAutoConfig ? 1 : undefined,
      },
    });

    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os backups do sistema.');
  }
}

export async function apiGetSystemBackupAutoConfig(token, params = {}) {
  try {
    const data = await apiListSystemBackups(token, {
      companyUuid: params?.companyUuid,
      includeAutoConfig: true,
    });

    return data?.auto_config || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar a configuracao do backup automatico.');
  }
}

export async function apiGetSystemBackupByUuid(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/backups_sistema.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        uuid_backup: params?.backupUuid,
      },
    });

    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o backup selecionado.');
  }
}

export async function apiCreateSystemBackup(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/backups_sistema.php', payload, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel criar o backup.');
  }
}

export async function apiUpdateSystemBackup(token, payload = {}, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.patch('/backups_sistema.php', payload, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        uuid_backup: params?.backupUuid,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar o backup.');
  }
}

export async function apiUpdateSystemBackupAutoConfig(token, payload = {}, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.patch('/backups_sistema.php', {
      ...payload,
      action: 'update_auto_config',
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
      },
    });

    return response?.data?.data?.auto_config || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a configuracao do backup automatico.');
  }
}

export async function apiDownloadSystemBackup(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/backups_sistema.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        uuid_backup: params?.backupUuid,
        download: 1,
      },
      responseType: 'blob',
    });

    const disposition = String(response?.headers?.['content-disposition'] || '');
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);

    return {
      blob: response?.data,
      filename: filenameMatch?.[1] || `backup_${params?.backupUuid || Date.now()}.bak`,
    };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel descarregar o arquivo de backup.');
  }
}

export async function apiRestoreSystemBackup(token, payload = {}, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/backups_sistema.php', {
      ...payload,
      action: 'restore',
      uuid_backup: params?.backupUuid || payload?.uuid_backup,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel restaurar o backup selecionado.');
  }
}

export async function apiDryRunRestoreSystemBackup(token, payload = {}, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/backups_sistema.php', {
      ...payload,
      action: 'dry_run_restore',
      uuid_backup: params?.backupUuid || payload?.uuid_backup,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel simular a restauracao do backup.');
  }
}

export async function apiDryRunRestoreLocalBackup(token, file, companyUuid) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const formData = new FormData();
    formData.append('action', 'dry_run_restore_local');
    formData.append('backup_file', file);

    const response = await api.post('/backups_sistema.php', formData, {
      withCredentials: true,
      headers: {
        ...(authorization ? { Authorization: authorization } : {}),
        'Content-Type': 'multipart/form-data',
      },
      params: { empresa_uuid: companyUuid },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel simular a restauracao do ficheiro local.');
  }
}

export async function apiRestoreLocalBackup(token, file, companyUuid) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const formData = new FormData();
    formData.append('action', 'restore_local');
    formData.append('backup_file', file);

    const response = await api.post('/backups_sistema.php', formData, {
      withCredentials: true,
      headers: {
        ...(authorization ? { Authorization: authorization } : {}),
        'Content-Type': 'multipart/form-data',
      },
      params: { empresa_uuid: companyUuid },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel restaurar o ficheiro de backup local.');
  }
}

export async function apiGetSystemBackupFilesAudit(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/backups_sistema.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        audit_files: 1,
      },
    });

    return response?.data?.data?.audit_files || { items: [], count: 0 };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel obter a auditoria de arquivos de backup.');
  }
}

// ─── Backups do painel admin (backups_admin.php) — admin_super, cross-tenant:
// base de dados inteira, qualquer empresa/loja, ou uma tabela específica ───

export async function apiListAdminBackups(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/backups_admin.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        scope_tipo: params?.scopeTipo,
        empresa_uuid: params?.companyUuid,
        estado: params?.estado,
        modo_disparo: params?.modoDisparo,
        pesquisa: params?.search,
        data_inicio: params?.dataInicio,
        data_fim: params?.dataFim,
        limit: params?.limit,
        offset: params?.offset,
      },
    });

    return response?.data?.data || { items: [], count: 0, total: 0, limit: 20, offset: 0, companies: [], tables: [], auto_config: null };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os backups do sistema.');
  }
}

export async function apiCreateAdminBackup(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/backups_admin.php', payload, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel criar o backup.');
  }
}

export async function apiDryRunRestoreAdminBackup(token, backupUuid) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/backups_admin.php', {
      action: 'dry_run_restore',
      uuid_backup: backupUuid,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel simular a restauracao do backup.');
  }
}

export async function apiRestoreAdminBackup(token, backupUuid) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/backups_admin.php', {
      action: 'restore',
      uuid_backup: backupUuid,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel restaurar o backup selecionado.');
  }
}

export async function apiDryRunRestoreAdminBackupLocal(token, file) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const formData = new FormData();
    formData.append('action', 'dry_run_restore_local');
    formData.append('backup_file', file);

    const response = await api.post('/backups_admin.php', formData, {
      withCredentials: true,
      headers: {
        ...(authorization ? { Authorization: authorization } : {}),
        'Content-Type': 'multipart/form-data',
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel simular a restauracao do ficheiro local.');
  }
}

export async function apiRestoreAdminBackupLocal(token, file) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const formData = new FormData();
    formData.append('action', 'restore_local');
    formData.append('backup_file', file);

    const response = await api.post('/backups_admin.php', formData, {
      withCredentials: true,
      headers: {
        ...(authorization ? { Authorization: authorization } : {}),
        'Content-Type': 'multipart/form-data',
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel restaurar o ficheiro de backup local.');
  }
}

export async function apiDownloadAdminBackup(token, backupUuid) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/backups_admin.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: { uuid_backup: backupUuid, download: 1 },
      responseType: 'blob',
    });

    const disposition = String(response?.headers?.['content-disposition'] || '');
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);

    return {
      blob: response?.data,
      filename: filenameMatch?.[1] || `backup_${backupUuid || Date.now()}.bak`,
    };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel descarregar o arquivo de backup.');
  }
}

export async function apiBulkDownloadAdminBackups(token, backupUuids) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/backups_admin.php', {
      action: 'bulk_download',
      uuid_backups: backupUuids,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      responseType: 'blob',
    });

    const disposition = String(response?.headers?.['content-disposition'] || '');
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);

    return {
      blob: response?.data,
      filename: filenameMatch?.[1] || `backups_${Date.now()}.zip`,
    };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel descarregar os backups selecionados.');
  }
}

export async function apiDeleteAdminBackup(token, backupUuid) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.delete('/backups_admin.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: { uuid_backup: backupUuid },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o backup.');
  }
}

export async function apiUpdateAdminBackupAutoConfig(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/backups_admin.php', {
      ...payload,
      action: 'update_auto_config',
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data?.auto_config || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a configuracao do backup automatico.');
  }
}

export async function apiGetDataRetentionStatus(token) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/data_retention_admin.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || { tables: [] };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar a configuracao de retencao de dados.');
  }
}

export async function apiUpdateDataRetentionConfig(token, { table, valor, auto } = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/data_retention_admin.php', {
      action: 'update_config',
      table,
      valor,
      auto,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a configuracao de retencao.');
  }
}

export async function apiPreviewDataRetentionPurge(token, { table, valor, companyUuid, storeUuid } = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/data_retention_admin.php', {
      action: 'preview_purge',
      table,
      valor,
      company_uuid: companyUuid,
      store_uuid: storeUuid,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || { count: 0 };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel simular a eliminacao.');
  }
}

export async function apiPurgeDataRetention(token, { table, valor, companyUuid, storeUuid } = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/data_retention_admin.php', {
      action: 'purge',
      table,
      valor,
      company_uuid: companyUuid,
      store_uuid: storeUuid,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || { deleted: 0 };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar os registos selecionados.');
  }
}

export async function apiListGastosSistema(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/gastos_sistema_admin.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        categoria: params?.categoria,
        pesquisa: params?.search,
        data_inicio: params?.dataInicio,
        data_fim: params?.dataFim,
        limit: params?.limit,
        offset: params?.offset,
      },
    });

    return response?.data?.data || { items: [], total: 0, limit: 20, offset: 0, total_valor_filtrado: 0, total_geral: 0, por_categoria: [], categorias: {} };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os gastos do sistema.');
  }
}

export async function apiCreateGastoSistema(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/gastos_sistema_admin.php', {
      categoria: payload?.categoria,
      descricao: payload?.descricao,
      valor_aoa: payload?.valorAoa,
      data_gasto: payload?.dataGasto,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registar o gasto.');
  }
}

export async function apiUpdateGastoSistema(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.put('/gastos_sistema_admin.php', {
      uuid_gasto: payload?.uuidGasto,
      categoria: payload?.categoria,
      descricao: payload?.descricao,
      valor_aoa: payload?.valorAoa,
      data_gasto: payload?.dataGasto,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar o gasto.');
  }
}

export async function apiDeleteGastoSistema(token, uuidGasto) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.delete('/gastos_sistema_admin.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      data: { uuid_gasto: uuidGasto },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o gasto.');
  }
}

export async function apiListGanhosSistema(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/ganhos_sistema_admin.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        categoria: params?.categoria,
        pesquisa: params?.search,
        data_inicio: params?.dataInicio,
        data_fim: params?.dataFim,
        limit: params?.limit,
        offset: params?.offset,
      },
    });

    return response?.data?.data || { items: [], total: 0, limit: 20, offset: 0, total_valor_filtrado: 0, total_geral: 0, por_categoria: [], categorias: {} };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os ganhos do sistema.');
  }
}

export async function apiCreateGanhoSistema(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/ganhos_sistema_admin.php', {
      categoria: payload?.categoria,
      descricao: payload?.descricao,
      valor_aoa: payload?.valorAoa,
      data_ganho: payload?.dataGanho,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registar o ganho.');
  }
}

export async function apiUpdateGanhoSistema(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.put('/ganhos_sistema_admin.php', {
      uuid_ganho: payload?.uuidGanho,
      categoria: payload?.categoria,
      descricao: payload?.descricao,
      valor_aoa: payload?.valorAoa,
      data_ganho: payload?.dataGanho,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar o ganho.');
  }
}

export async function apiDeleteGanhoSistema(token, uuidGanho) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.delete('/ganhos_sistema_admin.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      data: { uuid_ganho: uuidGanho },
    });

    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o ganho.');
  }
}

export async function apiGetConciliacaoSistema(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/conciliacao_admin.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        tipo: params?.tipo,
        categoria: params?.categoria,
        pesquisa: params?.search,
        data_inicio: params?.dataInicio,
        data_fim: params?.dataFim,
        limit: params?.limit,
        offset: params?.offset,
      },
    });

    return response?.data?.data || {
      items: [],
      total: 0,
      limit: 20,
      offset: 0,
      resumo: { total_ganhos_periodo: 0, total_gastos_periodo: 0, saldo_periodo: 0 },
      por_categoria_ganhos: [],
      por_categoria_gastos: [],
      por_mes: [],
      categorias_ganho: {},
      categorias_gasto: {},
    };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar a conciliação.');
  }
}

export async function apiListProducts(token, params = {}) {
  try {
    const response = await api.get('/produtos_servicos.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        // Sinal explícito para o backend: pedido deliberado de stock em
        // todas as lojas da empresa (não uma omissão acidental de
        // loja_uuid) — ver "Outras lojas da empresa" no POS.
        ...(params?.allStores ? { escopo_lojas: 'todas' } : {}),
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o catalogo.');
  }
}

export async function apiListCnbs(token, params = {}) {
  try {
    const response = await api.get('/cnbs.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        q: params?.q,
        limit: params?.limit,
        offset: params?.offset,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar a tabela CNBS.');
  }
}

export async function apiGetFinancialComplianceHub(token, params = {}) {
  try {
    const response = await api.get('/financial_compliance_hub.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        periodo: params?.period,
        consolidado: params?.consolidated ? 1 : undefined,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o dashboard fiscal.');
  }
}

export async function apiGetCeoDashboard(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/ceo_dashboard.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        periodo: params?.period,
        consolidado: params?.consolidated ? 1 : undefined,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o dashboard CEO.');
  }
}

export async function apiGetDreSummary(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/tesouraria/dre.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        consolidado: params?.consolidated ? 1 : undefined,
        data_inicio: params?.startDate,
        data_fim: params?.endDate,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar a DRE.');
  }
}

export async function apiGetPrevisaoIa(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/tesouraria/previsao_ia.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        consolidado: params?.consolidated ? 1 : undefined,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar a previsao com IA.');
  }
}

export async function apiGetProjecoesFinanceiras(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/tesouraria/projecoes.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        consolidado: params?.consolidated ? 1 : undefined,
        meses: params?.monthsForecast,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as projeccoes financeiras.');
  }
}

export async function apiGetProcurementDashboard(token, params = {}) {
  try {
    const response = await api.get('/procurement_dashboard.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        periodo: params?.period,
        consolidado: params?.consolidated ? 1 : undefined,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o painel de fornecedores.');
  }
}

export async function apiGetPurchasesDashboard(token, params = {}) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para carregar o dashboard de compras.');
    }

    const response = await api.get('/compras_dashboard.php', {
      headers: {
        Authorization: authorizationHeader,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        periodo: params?.period,
        invoices_page: params?.invoicesPage,
        movements_page: params?.movementsPage,
        invoices_page_size: params?.invoicesPageSize,
        movements_page_size: params?.movementsPageSize,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o dashboard de compras.');
  }
}

export async function apiGetProcurementReports(token, params = {}) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para carregar relatorios de fornecedores.');
    }

    const response = await api.get('/relatorios_procurement.php', {
      headers: {
        Authorization: authorizationHeader,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        periodo: params?.period,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os relatorios de fornecedores.');
  }
}

export async function apiGetSaftInventory(token, params = {}) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para carregar inventario SAF-T.');
    }

    const response = await api.get('/saft_inventario.php', {
      headers: {
        Authorization: authorizationHeader,
      },
      params: {
        // Opcionais: quando ambos informados, o backend passa a devolver
        // "movements" por produto limitado ao periodo (em vez do total
        // vitalicio) — usado pela exportacao SAF-T.
        data_inicio: params?.startDate || undefined,
        data_fim: params?.endDate || undefined,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o inventario SAF-T.');
  }
}

export async function apiSaveSaftInventorySnapshot(token, items = []) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para gravar inventario SAF-T.');
    }

    const response = await api.post('/saft_inventario.php', { items }, {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar o snapshot de inventario SAF-T.');
  }
}

export async function apiGetSaftExportHistory(token) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para carregar historico SAF-T.');
    }

    const response = await api.get('/saft_exportacoes.php', {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o historico de exportacoes SAF-T.');
  }
}

export async function apiCreateSaftExport(token, payload) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para registar exportacao SAF-T.');
    }

    const response = await api.post('/saft_exportacoes.php', payload, {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registar a exportacao SAF-T.');
  }
}

export async function apiGetSaftValidations(token) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para carregar validacoes SAF-T.');
    }

    const response = await api.get('/saft_validacoes.php', {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as validacoes SAF-T.');
  }
}

export async function apiCreateSaftValidation(token, payload) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para registar validacao SAF-T.');
    }

    const response = await api.post('/saft_validacoes.php', payload, {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registar a validacao SAF-T.');
  }
}

export async function apiResolveSaftValidation(token, payload = {}) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para resolver validacao SAF-T.');
    }

    const response = await api.post('/saft_validacoes.php', {
      action: 'resolve',
      issueId: payload?.issueId,
      code: payload?.code,
      type: payload?.type,
      title: payload?.title,
      description: payload?.description,
      severity: payload?.severity,
      autoFix: payload?.autoFix,
      products: payload?.products,
      resolved: payload?.resolved,
    }, {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a resolucao da validacao SAF-T.');
  }
}

export async function apiGetSaftSimulations(token) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para carregar simulacoes SAF-T.');
    }

    const response = await api.get('/saft_simulacoes.php', {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as simulacoes SAF-T.');
  }
}

export async function apiCreateSaftSimulation(token, payload) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para registar simulacao SAF-T.');
    }

    const response = await api.post('/saft_simulacoes.php', payload, {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registar a simulacao SAF-T.');
  }
}

export async function apiRunSaftSimulation(token, params = {}) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para executar simulacao SAF-T.');
    }

    const response = await api.post('/saft_simulacoes.php', {
      action: 'run-smart',
      type: params?.type,
      period: params?.period,
    }, {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel executar a simulacao inteligente SAF-T.');
  }
}

export async function apiGetSaftSimulationDetails(token, params = {}) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para carregar detalhes da simulacao SAF-T.');
    }

    const response = await api.post('/saft_simulacoes.php', {
      action: 'details-smart',
      type: params?.type,
      period: params?.period,
    }, {
      headers: {
        Authorization: authorizationHeader,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os detalhes da simulacao SAF-T.');
  }
}

export async function apiGetStoreManagerDashboard(token, params = {}, options = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/store_manager_dashboard.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        periodo: params?.period,
      },
      signal: options?.signal,
    });
    return response?.data?.data || null;
  } catch (error) {
    const normalizedMessage = String(error?.message || '').toLowerCase();
    const normalizedBackendMessage = String(error?.response?.data?.message || '').toLowerCase();
    const normalizedBackendError = String(error?.response?.data?.error || '').toLowerCase();
    const status = Number(error?.response?.status || 0);
    const combined = `${normalizedMessage} ${normalizedBackendMessage} ${normalizedBackendError}`;

    const isGenericOperationError = combined.includes('nao foi possivel concluir a operacao agora')
      || combined.includes('não foi possível concluir a operação agora');
    const isSqlOrDbError = /sqlstate|hy000|\[2002\]|invalid parameter number|integrity constraint|connection refused|econnrefused|liga[cç][aã]o [àa] base de dados|base de dados/i.test(combined);
    const isServerFailure = status >= 500;

    if (isGenericOperationError || isSqlOrDbError || isServerFailure) {
      const isLocalApiConfigured = /localhost|127\.0\.0\.1/i.test(String(api.defaults?.baseURL || ''));
      if (isLocalApiConfigured) {
        throw new Error('Nao foi possivel carregar o dashboard da loja porque a base de dados local nao respondeu. Verifique se o MySQL do XAMPP esta ativo e tente novamente.');
      }

      throw new Error('Nao foi possivel carregar o dashboard da loja porque o servidor nao respondeu corretamente. Tente novamente em instantes.');
    }

    throw buildApiError(error, 'Nao foi possivel carregar o dashboard do gerente de loja.');
  }
}

export async function apiGetAssistantBusinessContext(token, params = {}, options = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/assistant_business_context.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        period: params?.period,
        months: params?.months,
        question: params?.question,
      },
      signal: options?.signal,
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o contexto de negocio do assistente.');
  }
}

export async function apiSaveProduct(token, payload) {
  try {
    const method = payload?.uid_produto ? 'put' : 'post';
    const response = await api[method]('/produtos_servicos.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar o produto/servico.');
  }
}

export async function apiDeleteProduct(token, payload) {
  try {
    const response = await api.delete('/produtos_servicos.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });
    return response?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o produto/servico.');
  }
}

export async function apiListStockMovements(token, params = {}) {
  try {
    const response = await api.get('/movimentos_estoque.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        search: params?.search,
        type: params?.type,
        date_from: params?.dateFrom,
        date_to: params?.dateTo,
        page: params?.page,
        page_size: params?.pageSize,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os movimentos de estoque.');
  }
}

export async function apiCreateStockMovement(token, payload) {
  try {
    const response = await api.post('/movimentos_estoque.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registar o movimento de estoque.');
  }
}

export async function apiListStockTransferRequests(token, params = {}) {
  try {
    const response = await api.get('/solicitacoes_transferencia_estoque.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        estado: params?.status,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as solicitacoes de transferencia.');
  }
}

export async function apiCreateStockTransferRequest(token, payload) {
  try {
    const response = await api.post('/solicitacoes_transferencia_estoque.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel criar a solicitacao de transferencia.');
  }
}

export async function apiConfirmStockTransferRequest(token, payload) {
  try {
    const response = await api.patch('/solicitacoes_transferencia_estoque.php', {
      ...payload,
      action: 'confirm',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel confirmar a solicitacao de transferencia.');
  }
}

export async function apiReceiveStockTransferRequest(token, payload) {
  try {
    const response = await api.patch('/solicitacoes_transferencia_estoque.php', {
      ...payload,
      action: 'receive',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registrar o recebimento da transferencia.');
  }
}

export async function apiUpdateStockTransferRequestStatus(token, payload) {
  try {
    const response = await api.patch('/solicitacoes_transferencia_estoque.php', {
      ...payload,
      action: 'update-status',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar o estado da solicitacao.');
  }
}

export async function apiUpdateMinimumStock(token, payload) {
  try {
    const response = await api.put('/estoque_minimo.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel actualizar o stock minimo.');
  }
}

export async function apiListCustomers(token, params = {}) {
  try {
    const response = await api.get('/clientes.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os clientes.');
  }
}

export async function apiListInvoiceSeries(token, params = {}) {
  try {
    const response = await api.get('/series_fatura.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        search: params?.search,
        tipo_documento: params?.tipoDocumento,
        estado_serie: params?.estadoSerie,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as series da AGT.');
  }
}

export async function apiSaveInvoiceSeries(token, payload) {
  try {
    const method = payload?.id ? 'put' : 'post';
    logAgtDebug('series.request', {
      method: method.toUpperCase(),
      payload,
    });
    const response = await api[method]('/series_fatura.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    logAgtDebug('series.response', response?.data || null);
    return response?.data?.data;
  } catch (error) {
    const responsePayload = error?.response?.data || null;
    const apiErrors = formatStructuredApiErrors(responsePayload);

    logAgtDebug('series.error', {
      message: error?.message || null,
      status: error?.response?.status || null,
      response: responsePayload,
    });

    if (error?.response?.status >= 400) {
      console.error('[AGT] series.save.failed', {
        status: error?.response?.status || null,
        message: responsePayload?.message || error?.message || null,
        errors: apiErrors,
        response: responsePayload,
      });
      console.error('[AGT] series.save.failed.text', stringifyApiPayload({
        status: error?.response?.status || null,
        message: responsePayload?.message || error?.message || null,
        errors: apiErrors,
        response: responsePayload,
      }));
    }

    const fallback = apiErrors.length > 0
      ? apiErrors.join(' | ')
      : 'Nao foi possivel gravar a serie da AGT.';

    throw buildApiError(error, fallback);
  }
}

export async function apiDeleteInvoiceSeries(token, payload) {
  try {
    const response = await api.delete('/series_fatura.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });
    return response?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar a serie da AGT.');
  }
}

export async function apiGetAgtPrivateKeyStatus(token, params = {}) {
  try {
    const response = await api.get('/agt_chave_privada.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
      },
    });
    const data = response?.data?.data;

    if (data?.item && params?.companyUuid) {
      await apiSyncAgtPrivateKeyToLocalVault(token, {
        companyUuid: params.companyUuid,
        storeUuid: params?.storeUuid || data?.item?.storeUuid || null,
      });
    }

    return data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o estado da chave privada AGT.');
  }
}

export async function apiSaveAgtPrivateKey(token, payload) {
  try {
    const response = await api.post('/agt_chave_privada.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response?.data?.data;

    if (payload?.privateKey && payload?.companyUuid) {
      await storeLocalVaultPrivateKey({
        alias: buildContributorKeyAlias(payload.companyUuid),
        kind: 'contributor',
        privateKey: payload.privateKey,
        scope: 'current-user',
        companyUuid: payload.companyUuid,
        storeUuid: payload?.storeUuid || data?.item?.storeUuid || null,
        source: 'backend-save',
        metadata: {
          fingerprint: data?.item?.fingerprint || null,
          syncedAt: new Date().toISOString(),
        },
      });
    }

    return data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar a chave privada AGT.');
  }
}

export async function apiDeleteAgtPrivateKey(token, payload) {
  try {
    const response = await api.delete('/agt_chave_privada.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });

    if (payload?.companyUuid) {
      await removeLocalVaultPrivateKey(buildContributorKeyAlias(payload.companyUuid));
    }

    return response?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel remover a chave privada AGT.');
  }
}

export async function apiConsultAgtInvoice(token, payload) {
  try {
    const response = await api.post('/agt_consultar_fatura.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel consultar a fatura na AGT.');
  }
}

export async function apiConsultAgtDocumentsByDate(token, payload) {
  try {
    const response = await api.post('/agt_consultar_documentos.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: AGT_REQUEST_TIMEOUT_MS,
    });

    return {
      result: response?.data?.data?.result || null,
      payload: response?.data?.data?.payload || payload,
      fullResponse: response?.data?.data || null,
    };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel consultar documentos na AGT para o periodo informado.');
  }
}

export async function apiConsultAgtInvoiceStatus(token, payload) {
  try {
    const response = await api.post('/agt_consultar_estado_fatura.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel consultar o estado da fatura na AGT.');
  }
}

export async function apiSyncAgtPrivateKeyToLocalVault(token, params = {}) {
  if (!params?.companyUuid) {
    return null;
  }

  try {
    const response = await api.get('/agt_chave_privada.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params.companyUuid,
        include_private_key: 1,
      },
    });

    const item = response?.data?.data?.item || null;
    const privateKey = response?.data?.data?.privateKey || '';

    if (!item || !privateKey) {
      return { item, synced: false };
    }

    await storeLocalVaultPrivateKey({
      alias: buildContributorKeyAlias(params.companyUuid),
      kind: 'contributor',
      privateKey,
      scope: 'current-user',
      companyUuid: params.companyUuid,
      storeUuid: params?.storeUuid || item?.storeUuid || null,
      source: 'backend-sync',
      metadata: {
        fingerprint: item?.fingerprint || null,
        syncedAt: new Date().toISOString(),
      },
    });

    return { item, synced: true };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel sincronizar a chave privada AGT para o cofre local.');
  }
}

export async function apiGetProducerSoftware(token, params = {}) {
  try {
    const response = await api.get('/produtor_software.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        active_only: params?.activeOnly === false ? undefined : 1,
        id: params?.id,
        uuid_produtor: params?.uuidProdutor,
      },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o produtor de software.');
  }
}

export async function apiGetPublicProducerSoftware() {
  try {
    const response = await api.get('/produtor_software_publico.php');
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os dados publicos do produtor de software.');
  }
}

export async function apiGetTopEmpresasFaturacao(token, params = {}) {
  try {
    const response = await api.get('/top_empresas_faturacao_admin.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        mes: params?.mes || undefined,
        limit: params?.limit || undefined,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o top de empresas por faturacao.');
  }
}

export async function apiSaveProducerSoftware(token, payload) {
  try {
    const isUpdate = Boolean(payload?.uuid_produtor || payload?.uuidProdutor || payload?.id);
    const response = await api[isUpdate ? 'put' : 'post']('/produtor_software.php', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel guardar o produtor de software.');
  }
}

export async function apiListVideoTutorials(token) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/tutoriais_video.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data?.items || [];
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os tutoriais em video.');
  }
}

export async function apiCreateVideoTutorial(token, payload) {
  try {
    const response = await api.post('/tutoriais_video.php', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel criar o tutorial em video.');
  }
}

export async function apiUpdateVideoTutorial(token, uuid, payload) {
  try {
    const response = await api.patch('/tutoriais_video.php', { ...payload, uuid }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar o tutorial em video.');
  }
}

export async function apiDeleteVideoTutorial(token, uuid) {
  try {
    const response = await api.delete('/tutoriais_video.php', {
      headers: { Authorization: `Bearer ${token}` },
      data: { uuid },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o tutorial em video.');
  }
}

export async function apiGetSystemEnvSettings(token) {
  try {
    const response = await api.get('/system_env_admin.php', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.items || [];
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as configuracoes do sistema.');
  }
}

export async function apiUpdateSystemEnvSettings(token, updates) {
  try {
    const response = await api.post('/system_env_admin.php', updates, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.items || [];
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar as configuracoes do sistema.');
  }
}

function buildProducerSigningHeaders(producerSoftware) {
  if (!producerSoftware) {
    return {};
  }

  return {
    producer_uuid: producerSoftware?.uuidProdutor || null,
    software_name: producerSoftware?.nomeSoftware || null,
    software_version: producerSoftware?.versaoAtual || null,
    software_validation_number: producerSoftware?.numeroValidacaoAgt || null,
    software_schema_version: producerSoftware?.versaoSchema || null,
  };
}

export async function apiSignFiscalDocument(token, payload) {
  const companyUuid = payload?.companyUuid || payload?.uuid_empresa || null;
  const documentPayload = payload?.documento || payload?.payload || null;
  let producerSoftware = null;

  if (token) {
    try {
      const producerData = await apiGetProducerSoftware(token, { activeOnly: true });
      producerSoftware = producerData?.activeItem || producerData?.item || null;
    } catch {
      producerSoftware = null;
    }
  }

  if (companyUuid && documentPayload) {
    try {
      const signedDocument = await signJsonWithPreferredLocalVault({
        companyUuid,
        payload: documentPayload,
        headers: {
          use: 'fiscal-document',
          company_uuid: companyUuid,
          ...buildProducerSigningHeaders(producerSoftware),
        },
      });

      if (signedDocument) {
        return {
          signedDocument,
          producerSoftware,
          usedLocalVault: true,
        };
      }
    } catch {
      // Se nao houver chave local ou o cofre falhar, recorre ao backend quando disponivel.
    }
  }

  try {
    const response = await api.post('/agt_assinar_documento.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return {
      ...(response?.data?.data || {}),
      usedLocalVault: false,
    };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel assinar o documento fiscal.');
  }
}

export async function apiSaveCustomer(token, payload) {
  try {
    const method = payload?.id ? 'put' : 'post';
    const response = await api[method]('/clientes.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar o cliente.');
  }
}

export async function apiDeleteCustomer(token, payload) {
  try {
    const response = await api.delete('/clientes.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });
    return response?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o cliente.');
  }
}

export async function apiListCustomerStatements(token, params = {}) {
  try {
    const response = await api.get('/extrato_clientes.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        customer_id: params?.customerId,
        customer_uuid: params?.customerUuid,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o extrato do cliente.');
  }
}

export async function apiSaveCustomerStatement(token, payload) {
  try {
    const method = payload?.id ? 'put' : 'post';
    const response = await api[method]('/extrato_clientes.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar o lançamento do extrato.');
  }
}

export async function apiDeleteCustomerStatement(token, payload) {
  try {
    const response = await api.delete('/extrato_clientes.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });
    return response?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o lançamento do extrato.');
  }
}

export async function apiListSuppliers(token, params = {}) {
  try {
    const response = await api.get('/fornecedores.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        utilizador_uuid: params?.userUuid,
        search: params?.search,
        status: params?.status,
        categoria: params?.category,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os fornecedores.');
  }
}

export async function apiGetSupplierById(token, id, params = {}) {
  try {
    const response = await api.get('/fornecedores.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        id,
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        utilizador_uuid: params?.userUuid,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o fornecedor.');
  }
}

export async function apiSaveSupplier(token, payload) {
  try {
    const method = payload?.id ? 'put' : 'post';
    const response = await api[method]('/fornecedores.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar o fornecedor.');
  }
}

export async function apiDeleteSupplier(token, payload) {
  try {
    const response = await api.delete('/fornecedores.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });
    return response?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o fornecedor.');
  }
}

export async function apiListSupplierCategories(token, params = {}) {
  const endpoints = ["/categorias_fornecedor.php", "/fornecedores/categorias_fornecedor.php"];
  const requestParams = {
    empresa_uuid: params?.companyUuid,
    loja_uuid: params?.storeUuid,
    utilizador_uuid: params?.userUuid,
    search: params?.search,
    status: params?.status,
  };

  const executeListRequest = async () => {
    let lastError = null;
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error("Sessão inválida ou expirada. Reautentique para continuar.");
    }

    for (const endpoint of endpoints) {
      try {
        return await api.get(endpoint, {
          headers: {
            Authorization: authorizationHeader,
          },
          params: requestParams,
        });
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError;
  };

  try {
    const response = await executeListRequest();
    const data = response?.data?.data;
    const shouldAutoInsertDefault = (params?.autoInsertDefault ?? true) && !params?.search && !params?.status;
    const hasScope = Boolean(String(params?.companyUuid || "").trim());
    const items = Array.isArray(data?.items) ? data.items : [];

    if (shouldAutoInsertDefault && hasScope && items.length === 0) {
      try {
        await apiSaveSupplierCategory(token, {
          name: "Geral",
          description: "Categoria criada automaticamente.",
          color: "#6366f1",
          icon: "Briefcase",
          status: "Activa",
          companyUuid: params?.companyUuid,
          storeUuid: params?.storeUuid,
          userUuid: params?.userUuid,
        });

        const refreshed = await executeListRequest();
        return refreshed?.data?.data;
      } catch (seedError) {
        logAgtDebug("supplier-categories.auto-insert.error", {
          message: seedError?.message,
          status: seedError?.response?.status,
          apiPayload: seedError?.response?.data,
        });
      }
    }

    return data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as categorias de fornecedor.');
  }
}

export async function apiSaveSupplierCategory(token, payload) {
  try {
    const method = payload?.id ? 'put' : 'post';
    const endpoints = ["/categorias_fornecedor.php", "/fornecedores/categorias_fornecedor.php"];
    let lastError = null;
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error("Sessão inválida ou expirada. Reautentique para continuar.");
    }

    console.log('[apiSaveSupplierCategory]', `Method: ${method}`, 'Payload:', JSON.stringify(payload, null, 2));

    for (const endpoint of endpoints) {
      try {
        const response = await api[method](endpoint, payload, {
          headers: {
            Authorization: authorizationHeader,
          },
        });
        return response?.data?.data;
      } catch (error) {
        lastError = error;
        console.error('[apiSaveSupplierCategory] Error for endpoint', endpoint, error?.response?.status, error?.response?.data);
      }
    }

    throw lastError;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar a categoria de fornecedor.');
  }
}

export async function apiDeleteSupplierCategory(token, payload) {
  try {
    const endpoints = ["/categorias_fornecedor.php", "/fornecedores/categorias_fornecedor.php"];
    let lastError = null;
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error("Sessão inválida ou expirada. Reautentique para continuar.");
    }

    for (const endpoint of endpoints) {
      try {
        const response = await api.delete(endpoint, {
          headers: {
            Authorization: authorizationHeader,
          },
          data: payload,
        });
        return response?.data;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar a categoria de fornecedor.');
  }
}

export async function apiListSupplierContracts(token, params = {}) {
  try {
    const response = await api.get('/contratos_fornecedores.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        utilizador_uuid: params?.userUuid,
        search: params?.search,
        status: params?.status,
        fornecedor_uuid: params?.supplierUuid,
        categoria_uuid: params?.categoryUuid,
        periodo: params?.period,
        date_from: params?.dateFrom,
        date_to: params?.dateTo,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os contratos de fornecedor.');
  }
}

export async function apiSaveSupplierContract(token, payload) {
  try {
    const method = payload?.id ? 'put' : 'post';
    const response = await api[method]('/contratos_fornecedores.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar o contrato de fornecedor.');
  }
}

export async function apiDeleteSupplierContract(token, payload) {
  try {
    const response = await api.delete('/contratos_fornecedores.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });
    return response?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o contrato de fornecedor.');
  }
}

export async function apiListSupplierPurchases(token, params = {}) {
  try {
    const response = await api.get('/compras_fornecedores.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        utilizador_uuid: params?.userUuid,
        id: params?.id,
        periodo: params?.period,
        date_from: params?.dateFrom,
        date_to: params?.dateTo,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as compras de fornecedor.');
  }
}

export async function apiSaveSupplierPurchase(token, payload) {
  try {
    const response = await api.post('/compras_fornecedores.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registar a compra de fornecedor.');
  }
}

export async function apiUpdateSupplierPurchase(token, payload) {
  try {
    const response = await api.patch('/compras_fornecedores.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a compra de fornecedor.');
  }
}

export async function apiDeleteSupplierPurchase(token, payload = {}) {
  try {
    const response = await api.delete('/compras_fornecedores.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: payload,
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel cancelar a compra de fornecedor.');
  }
}

export async function apiListContractAttachments(token, params = {}) {
  try {
    const response = await api.get('/contratos_anexos.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        empresa_uuid: params?.companyUuid,
        contrato_uuid: params?.contractUuid,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os anexos do contrato.');
  }
}

export async function apiUploadContractAttachments(token, contractUuid, companyUuid, files, onUploadProgress) {
  try {
    const formData = new FormData();
    formData.append('contractUuid', contractUuid);
    formData.append('empresa_uuid', companyUuid);
    for (const file of files) {
      formData.append('files[]', file);
    }
    const response = await api.post('/contratos_anexos.php', formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os ficheiros.');
  }
}

export async function apiDeleteContractAttachment(token, attachmentUuid, companyUuid) {
  try {
    const response = await api.delete('/contratos_anexos.php', {
      headers: { Authorization: `Bearer ${token}` },
      data: { uuid: attachmentUuid, companyUuid },
    });
    return response?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel eliminar o anexo.');
  }
}

export async function apiDownloadContractAttachment(token, attachmentUuid, companyUuid) {
  try {
    const response = await api.get('/contratos_anexos.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: { action: 'download', uuid: attachmentUuid, empresa_uuid: companyUuid },
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel fazer o download do anexo.');
  }
}

export async function apiGetDailyReport(token, params = {}) {
  try {
    const response = await api.get('/relatorio_diario.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        date: params?.date,
        company_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        store_uuid: params?.storeUuid,
        user_uuid: params?.userUuid,
        estado_relatorio: params?.status,
      },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o relatório diário do POS.');
  }
}

export async function apiGetDailyReportByUuid(token, uuid) {
  try {
    const response = await api.get('/relatorio_diario.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        uuid,
      },
    });
    return response?.data?.data?.item || response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o relatório diário por identificador.');
  }
}

export async function apiListDailyReports(token, params = {}) {
  try {
    const response = await api.get('/relatorio_diario.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        list: 1,
        company_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        store_uuid: params?.storeUuid,
        date_from: params?.dateFrom,
        date_to: params?.dateTo,
        user_uuid: params?.userUuid,
        estado_relatorio: params?.status,
      },
    });
    return response?.data?.data?.items || [];
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o histórico do relatório diário.');
  }
}

export async function apiSaveDailyReport(token, payload) {
  try {
    const method = payload?.uuid_fecho || payload?.uuidFecho ? 'put' : 'post';
    const response = await api[method]('/relatorio_diario.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar o saldo inicial do POS.');
  }
}

export async function apiListSales(token, params = {}) {
  try {
    const response = await api.get('/vendas.php', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        user_uuid: params?.userUuid,
        limit: params?.limit,
        fiscal_gain_only: params?.fiscal_gain_only,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as vendas.');
  }
}

export async function apiStartSale(token, payload) {
  try {
    const response = await api.post('/vendas.php', {
      ...payload,
      action: 'iniciar_venda',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data?.item;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel iniciar a venda.');
  }
}

export async function apiCancelStartedSale(token, payload) {
  try {
    const response = await api.post('/vendas.php', {
      ...payload,
      action: 'cancelar_venda_iniciada',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel cancelar a venda iniciada.');
  }
}

export async function apiUpdateStartedSaleInternalNote(token, payload) {
  try {
    const response = await api.post('/vendas.php', {
      ...payload,
      action: 'atualizar_observacao_interna_venda_iniciada',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel atualizar a observacao interna da venda iniciada.');
  }
}

export async function apiSetStartedSaleOnHold(token, payload) {
  try {
    const response = await api.post('/vendas.php', {
      ...payload,
      action: 'deixar_venda_em_espera',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel colocar a venda em espera.');
  }
}

export async function apiResolveActiveSale(token, payload) {
  try {
    const response = await api.post('/vendas.php', {
      ...payload,
      action: 'resolver_venda_ativa',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel resolver a venda activa.');
  }
}

export async function apiRecoverActiveSaleByUser(token, payload) {
  try {
    const response = await api.post('/vendas.php', {
      ...payload,
      action: 'recuperar_venda_ativa_utilizador',
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel recuperar a venda activa do utilizador.');
  }
}

export async function apiSaveSale(token, payload) {
  try {
    const method = payload?.uuid_venda || payload?.uuidVenda ? 'put' : 'post';
    const response = await api[method]('/vendas.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const responseData = response?.data?.data || null;
    const item = responseData?.item || null;
    let agtSync = responseData?.agtSync || null;

    const estadoVendaNormalizado = String(item?.estado_venda || "").trim().toLowerCase();
    const vendaFinalizada = ["confirmada", "concluido", "concluida", "finalizado", "finalizada"].includes(estadoVendaNormalizado);

    if (!agtSync && vendaFinalizada && item?.uuid_venda) {
      try {
        const syncData = await apiSyncTransacoesAgt(token, {
          uuid_venda: item.uuid_venda,
        });
        const firstResult = Array.isArray(syncData?.results) ? (syncData.results[0] || null) : null;
        agtSync = firstResult || {
          success: false,
          skipped: false,
          message: "Sincronização AGT não retornou resultado para a venda finalizada.",
          requestID: null,
          eacCodigo: null,
          errors: [],
        };
      } catch (syncError) {
        agtSync = {
          success: false,
          skipped: false,
          message: resolveApiError(syncError, "Venda finalizada localmente, mas falhou a sincronização AGT."),
          requestID: null,
          eacCodigo: null,
          errors: [],
        };
      }
    }

    if (agtSync) {
      console.groupCollapsed('[AGT] Resultado sincronizacao venda');
      console.log('success:', agtSync?.success);
      console.log('message:', agtSync?.message);
      console.log('requestID:', agtSync?.requestID);
      console.log('errors:', agtSync?.errors || []);
      console.log('trace.requestPayload:', agtSync?.trace?.requestPayload || null);
      console.log('trace.responsePayload:', agtSync?.trace?.responsePayload || null);
      console.log('trace.requestID:', agtSync?.trace?.requestID || null);
      console.groupEnd();
    } else {
      console.log('[AGT] Sem bloco agtSync no retorno de /vendas.php');
    }

    if (item) {
      return {
        ...item,
        agtSync,
      };
    }

    return responseData;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gravar a venda.');
  }
}

export async function apiListTransacoes(token, params = {}) {
  try {
    const response = await api.get('/faturacao/transacoes.php', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response?.data?.data || { items: [], pagination: { page: 1, limit: 30, total: 0, total_pages: 1 } };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as transações.');
  }
}

export async function apiGetTransacaoDetail(token, uuidVenda, options = {}) {
  try {
    const response = await api.get('/faturacao/transacoes.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        uuid_venda: uuidVenda,
        empresa_uuid: options?.companyUuid || undefined,
        loja_uuid: options?.storeUuid || undefined,
      },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar a segunda via da transação.');
  }
}

export async function apiSyncTransacoesAgt(token, payload = {}) {
  try {
    const response = await api.post('/faturacao/transacoes.php', {
      action: 'sincronizar_agt_transacoes',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response?.data?.data || {
      total: 0,
      sincronizadas: 0,
      ignoradas: 0,
      falhas: 0,
      results: [],
    };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível sincronizar as transações com a AGT.');
  }
}

export async function apiGetAgtVerificationAlerts(token, params = {}) {
  try {
    const response = await api.get('/faturacao/agt_verification_alerts.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        loja_uuid: params?.storeUuid || undefined,
        consolidado: params?.consolidated ? 1 : undefined,
      },
    });
    return response?.data?.data || { count: 0, items: [] };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os alertas de verificação AGT.');
  }
}

// ── Notificações Push ────────────────────────────────────────────────────

export async function apiGetPushConfig() {
  try {
    // withCredentials:false explicito — mesmo motivo que apiVerificarDocumento
    // acima: a instância partilhada 'api' usa withCredentials:true por
    // omissão, mas push_config.php é propositadamente público (só devolve a
    // chave VAPID pública, sem sessão) e não envia
    // Access-Control-Allow-Credentials:true na resposta. Sem esta
    // substituição, o navegador bloqueia o pedido por CORS antes de chegar à
    // resposta.
    // skipAutoAuth:true — o interceptor (ver api.interceptors.request acima)
    // anexa Authorization a todo pedido por omissão; push_config.php restringe
    // Access-Control-Allow-Headers só a Content-Type (não precisa de sessão),
    // por isso um Authorization anexado automaticamente rebenta o preflight.
    const response = await api.get('/push_config.php', { withCredentials: false, skipAutoAuth: true });
    return response?.data?.data || { vapidPublicKey: null, pushEnabled: false };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar a configuração de notificações push.');
  }
}

export async function apiSubscribePush(token, payload = {}) {
  try {
    const response = await api.post('/notificacoes/push_subscribe.php', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível ativar as notificações push.');
  }
}

export async function apiUnsubscribePush(token, deviceId) {
  try {
    const response = await api.post('/notificacoes/push_unsubscribe.php', { deviceId }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || { success: true };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível desativar as notificações push.');
  }
}

export async function apiGetNotificationPreferences(token) {
  try {
    const response = await api.get('/notificacoes/notification_preferences.php', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as preferências de notificação.');
  }
}

export async function apiSaveNotificationPreferences(token, preferences = {}) {
  try {
    const response = await api.patch('/notificacoes/notification_preferences.php', preferences, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível guardar as preferências de notificação.');
  }
}

// Painel admin — dispara uma notificação push de teste para o próprio
// utilizador autenticado, para confirmar entrega sem precisar de um evento
// de negócio real (equivalente por HTTP a scripts/send-test-push-notification.php).
export async function apiSendTestPushNotification(token, category = 'push_alerta') {
  try {
    const response = await api.post('/notificacoes/push_test.php', { category }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível enviar a notificação de teste.');
  }
}

export async function apiPreviewAgtPayload(token, uuidVenda, options = {}) {
  try {
    const response = await api.post('/faturacao/transacoes.php', {
      action: 'preview_agt_payload',
      uuid_venda: uuidVenda,
      companyUuid: options?.companyUuid || undefined,
      storeUuid: options?.storeUuid || undefined,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível pré-visualizar o payload AGT.');
  }
}

export async function apiPrepareTransacaoPosConversion(token, payload = {}) {
  try {
    const response = await api.post('/faturacao/transacoes.php', {
      action: 'preparar_conversao_pos',
      uuid_venda: payload?.uuid_venda || payload?.uuidVenda,
      tipo_destino: payload?.tipo_destino || payload?.tipoDestino || 'FR',
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível preparar a conversão para o POS.');
  }
}

export async function apiCreateEmpresa(token, payload = {}) {
  try {
    const response = await api.post('/empresas.php', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel criar a empresa.');
  }
}

export async function apiListSessionLogs(token, { limit = 20, offset = 0, search = '', apenasAtivas = false, estado = '', empresaUuid = '', dateFrom = '', dateTo = '' } = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const params = { limit, offset };
    if (search) params.search = search;
    if (estado) params.estado = estado;
    else if (apenasAtivas) params.apenas_ativas = '1';
    if (empresaUuid) params.empresa_uuid = empresaUuid;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const response = await api.get('/sessoes.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params,
    });
    return response?.data?.data || { sessoes: [], total: 0 };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar as sessoes.');
  }
}

export async function apiRegisterAppClientVersion(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/app_client_versions.php', payload, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel registar a versao da aplicacao.');
  }
}

export async function apiTrackObservabilityEvent(token, payload = {}, options = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    if (!authorization && options?.allowWithoutAuth !== true) {
      return null;
    }

    const response = await api.post('/observability_events.php', payload, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data || null;
  } catch (error) {
    if (options?.silent !== false) {
      return null;
    }

    throw buildApiError(error, 'Nao foi possivel registar evento de observabilidade.');
  }
}

export async function apiListObservabilityEvents(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/observability_events.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        limit: params?.limit,
        module: params?.module,
        event: params?.event,
        severity: params?.severity,
      },
    });

    return response?.data?.data || { items: [], count: 0 };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar os eventos de observabilidade.');
  }
}

export async function apiPurgeObservabilityEvents(token, dias = 3) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/observability_events.php', { action: 'purgar_antigos', dias }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel purgar os eventos de observabilidade.');
  }
}

// Elimina registos duplicados de app_client_versions (o mesmo utilizador+
// empresa com várias linhas — normalmente porque device_id mudou entre
// instalações), mantendo só a mais recente por grupo. admin_super apenas.
export async function apiPurgeDuplicateClientVersions(token, companyUuid = '') {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/app_client_versions.php', {
      action: 'purge_duplicate_clients',
      company_uuid: companyUuid || undefined,
    }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível limpar os clientes duplicados.');
  }
}

export async function apiListAppClientVersions(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/app_client_versions.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        company_uuid: params?.companyUuid,
        store_uuid: params?.storeUuid,
        status: params?.status,
        search: params?.search,
        limit: params?.limit,
        offset: params?.offset,
        stale_hours: params?.staleHours,
      },
    });
    return response?.data?.data || { summary: {}, items: [], filters: { companies: [], stores: [] }, pagination: { limit: 15, offset: 0, total: 0, total_pages: 0 }, stale_required_items: [] };
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o painel de versoes das apps.');
  }
}

export async function apiGetAppReleaseControl(token) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/app_release_control.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o cenario de atualizacao.');
  }
}

export async function apiGetAppReleasePreview(token, computeChecksum = false) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const query = `?preview=1&compute_checksum=${computeChecksum ? 1 : 0}`;
    const response = await api.get(`/app_release_control.php${query}`, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      timeout: 20000,
    });
    const data = response?.data || null;

    if (data && !data.manifest && data.data && typeof data.data === 'object') {
      const payload = data.data;
      return {
        ...data,
        preview: true,
        manifest: {
          version: String(payload?.target_version || ''),
          notes: String(payload?.manifest_notes || ''),
          pub_date: String(payload?.manifest_pub_date || ''),
          platforms: {
            'windows-x86_64': {
              signature: String(payload?.windows_signature || ''),
              url: String(payload?.windows_url || ''),
            },
          },
        },
        checks: {},
      };
    }

    return data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel gerar preview do manifesto.');
  }
}

export async function apiPublishAppRelease(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/app_release_control.php', { ...payload, action: 'publish' }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel publicar o manifesto de atualizacao.');
  }
}

// Força a atualização automática nos clientes desktop já ligados (admin_super
// apenas — mesma gate de apiPublishAppRelease/apiRollbackAppRelease). Não é
// push instantâneo: o cliente faz polling leve e periódico, normalmente
// dentro de 1 minuto (ver Header.jsx).
export async function apiForceDesktopUpdate(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/app_release_control.php', { ...payload, action: 'force_update' }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível forçar a atualização nos clientes desktop.');
  }
}

export async function apiRollbackAppRelease(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/app_release_control.php', { ...payload, action: 'rollback' }, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel efetuar rollback do manifesto.');
  }
}

export async function apiListAppReleaseAuditoria(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/app_release_control.php', {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        auditoria: 1,
        limit: params?.limit || 20,
      },
    });
    return response?.data?.data?.items || [];
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o historico de alteracoes do cenario de atualizacao.');
  }
}

export async function apiUpdateAppReleaseControl(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/app_release_control.php', payload, {
      withCredentials: true,
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel salvar o cenario de atualizacao.');
  }
}

export async function apiRevokeSession(token, uuidSessao) {
  try {
    const response = await api.post('/sessoes.php', { action: 'revogar', uuid_sessao: uuidSessao }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel revogar a sessao.');
  }
}

export async function apiPurgeExpiredSessions(token) {
  try {
    const response = await api.post('/sessoes.php', { action: 'purgar_expiradas' }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel purgar as sessoes expiradas.');
  }
}

export async function apiRevokeSessionsBulk(token, uuidSessoes = []) {
  try {
    const response = await api.post('/sessoes.php', { action: 'revogar_lote', uuid_sessoes: uuidSessoes }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel revogar as sessoes selecionadas.');
  }
}

export async function apiRevokeUserSessions(token, uuidUtilizador) {
  try {
    const response = await api.post('/sessoes.php', { action: 'revogar_utilizador', uuid_utilizador: uuidUtilizador }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel revogar as sessoes deste utilizador.');
  }
}

export async function apiPreviewAgtInvoiceDecision(token, payload = {}) {
  try {
    const uuidVenda = payload?.uuidVenda || payload?.uuid_venda || null;

    if (uuidVenda) {
      const previewData = await apiPreviewAgtPayload(token, uuidVenda);
      return {
        ...previewData,
        payload: previewData?.requestPayload || previewData?.payload || null,
      };
    }

    const response = await api.post('/agt_validar_documento.php', {
      ...payload,
      previewOnly: true,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível pré-visualizar a decisão da fatura na AGT.');
  }
}

export async function apiSubmitAgtInvoiceDecision(token, payload = {}) {
  try {
    const response = await api.post('/agt_validar_documento.php', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível enviar a decisão da fatura na AGT.');
  }
}

export async function apiRetificarEmitirNc(token, payload = {}) {
  try {
    const response = await api.post('/faturacao/transacoes.php', {
      action: 'retificar_emitir_nc',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível retificar e emitir a Nota de Crédito.');
  }
}

export async function apiCancelarVendaCompleta(token, payload = {}) {
  try {
    const response = await api.post('/faturacao/transacoes.php', {
      action: 'cancelar_venda_completa',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível cancelar a venda.');
  }
}

export async function apiEmitirDocumentoLogistico(token, payload = {}) {
  try {
    const response = await api.post('/documentos_logisticos.php', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível registar o documento logístico.');
  }
}

export async function apiListDocumentosLogisticos(token, params = {}) {
  try {
    const response = await api.get('/documentos_logisticos.php', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response?.data?.data || { items: [], total: 0 };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os documentos logísticos.');
  }
}

export async function apiListPosExpenses(token, params = {}) {
  try {
    const response = await api.get('/despesas_pos.php', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response?.data?.data || { items: [], total: 0 };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as despesas do POS.');
  }
}

export async function apiGetPosSalesExpenseTrend(token, params = {}) {
  try {
    const response = await api.get('/pos_sales_expense_trend.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        user_uuid: params?.userUuid,
        days: params?.days,
        scope: params?.scope,
      },
    });

    return response?.data?.data || {
      items: [],
      totals: { sales: 0, returns: 0, expenses: 0 },
      range: { dateFrom: null, dateTo: null, days: 0 },
      companyUuid: params?.companyUuid || null,
      storeUuid: params?.storeUuid || null,
      userUuid: params?.userUuid || null,
      scope: params?.scope || 'operacional',
    };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar a tendência de vendas e despesas do POS.');
  }
}

export async function apiCreatePosExpense(token, payload = {}) {
  try {
    const response = await api.post('/despesas_pos.php', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível registar a despesa do POS.');
  }
}

export async function apiValidatePosExpense(token, payload = {}) {
  try {
    const response = await api.post('/despesas_pos_validate.php', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || { canExpense: false, reason: 'Erro na validação', availableCash: 0 };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível validar a despesa do POS.');
  }
}

export async function apiListSoldItems(token, params = {}) {
  try {
    const response = await api.get('/produtos_vendidos.php', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response?.data?.data || { items: [], summary: {} };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os itens vendidos.');
  }
}

export async function apiListCobrancas(token, params = {}) {
  try {
    const response = await api.get('/faturacao/cobrancas.php', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response?.data?.data || { items: [], summary: {} };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as cobranças.');
  }
}

export async function apiEmitirRecibo(token, payload) {
  try {
    const response = await api.post('/faturacao/cobrancas.php', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível emitir o recibo.');
  }
}

export async function apiFetchFaturaItems(token, uuidVenda) {
  try {
    const response = await api.get('/faturacao/cobrancas.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: { uuid_venda_items: uuidVenda },
    });
    return response?.data?.data?.items || [];
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os itens da fatura.');
  }
}

export async function apiConsultAgtTaxpayer(token, nif) {
  try {
    const effectiveToken = String(token || resolvePersistedAuthToken()).trim();
    const normalizedNif = String(nif || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .trim();

    if (!effectiveToken) {
      throw new Error('Sessão inválida ou expirada para consultar a AGT.');
    }

    if (!normalizedNif) {
      throw new Error('NIF inválido para consulta AGT.');
    }

    const response = await api.post('/agt_contribuinte.php', {
      nif: normalizedNif,
      tipoDocumento: 'NIF',
      numeroDocumento: normalizedNif,
    }, {
      headers: {
        Authorization: `Bearer ${effectiveToken}`,
      },
      timeout: AGT_REQUEST_TIMEOUT_MS,
    });
    return response?.data?.data;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel consultar o contribuinte na AGT.');
  }
}

export async function apiGetCatalogProducts(token) {
  try {
    const authorizationHeader = buildAuthorizationHeader(token);
    if (!authorizationHeader) {
      throw new Error('Sessao invalida para carregar o catálogo de produtos.');
    }
    const response = await api.get('/catalogo/produtos.php', {
      headers: {
        Authorization: authorizationHeader,
      },
    });
    return response?.data?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Nao foi possivel carregar o catálogo de produtos.');
  }
}

export async function apiListContasPagar(token, params = {}) {
  try {
    const response = await api.get('/tesouraria/contas_pagar.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        estado: params?.status,
        search: params?.search,
        consolidado: params?.consolidated ? 1 : undefined,
      },
    });
    return response?.data?.data?.items || [];
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as contas a pagar.');
  }
}

export async function apiAgendarContaPagar(token, payload = {}) {
  try {
    const response = await api.put('/tesouraria/contas_pagar.php', {
      action: 'agendar',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível agendar o pagamento.');
  }
}

export async function apiConfirmarPagamentoContaPagar(token, payload = {}) {
  try {
    const response = await api.put('/tesouraria/contas_pagar.php', {
      action: 'confirmar',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível confirmar o pagamento.');
  }
}

// Movimentos do Cofre (numerário físico) — usado para saber quanto dinheiro
// vivo está disponível antes de confirmar um pagamento em numerário, e para
// registar a saída correspondente. Nota: movimentos_cofre.php usa um
// envelope legado {sucesso, dados/erro} em vez do {success, data/message}
// do resto da API — daí o acesso directo a response?.data?.dados abaixo.
export async function apiListMovimentosCofre(token, params = {}) {
  try {
    const response = await api.get('/movimentos_cofre.php', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return Array.isArray(response?.data?.dados) ? response.data.dados : [];
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os movimentos do cofre.');
  }
}

export async function apiRegistrarMovimentoCofre(token, payload = {}) {
  try {
    const response = await api.post('/movimentos_cofre.php', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.dados || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível registar o movimento no cofre.');
  }
}

export async function apiAprovarMovimentoCofre(token, historicoUuid) {
  try {
    const response = await api.post('/movimentos_cofre.php', { action: 'aprovar', uuid: historicoUuid }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.dados || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível aprovar o movimento.');
  }
}

// Saldo autoritativo (cash/bank) calculado no servidor —
// CofreRepository::computeSaldo(), a única fórmula real. Substitui os
// vários recálculos locais que existiam em paralelo (frontend do
// Numerário, pagamento em numerário em Contas a Pagar) e que podiam
// divergir entre si.
export async function apiGetSaldoCofre(token, params = {}) {
  try {
    const response = await api.get('/movimentos_cofre.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        mode: 'saldo',
        loja_uuid: params?.storeUuid,
        consolidado: params?.consolidated ? 1 : undefined,
      },
    });
    return response?.data?.dados || { cash: 0, bank: 0 };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível calcular o saldo do cofre.');
  }
}

// Dinheiro do banco (TPA/transferência) recebido em vendas e já confirmado
// nos fechos de caixa — ver RelatorioDiarioRepository::sumTotalBancoFechado().
// Distinto dos movimentos manuais do Cofre (entrada_banco/saida_banco), que
// já ficam ligados a uma conta bancária específica.
export async function apiGetSaldoBancarioFechado(token, params = {}) {
  try {
    const response = await api.get('/tesouraria/saldo_bancario.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        empresa_uuid: params?.companyUuid,
        loja_uuid: params?.storeUuid,
        consolidado: params?.consolidated ? 1 : undefined,
      },
    });
    return response?.data?.data || { total: 0, totalRelatorios: 0 };
  } catch (error) {
    throw buildApiError(error, 'Não foi possível calcular o saldo bancário das vendas.');
  }
}

// Lança uma despesa manual em Contas a Pagar (imposto, dívida interna,
// horas extras, despesa com cliente, etc.) — mesma tabela/fluxo de
// pagamento das contas de fornecedor, só sem uma compra de fornecedor por
// trás. Ver ContasPagarRepository::criarDespesaManual().
export async function apiCriarDespesaContaPagar(token, payload = {}) {
  try {
    const response = await api.put('/tesouraria/contas_pagar.php', {
      action: 'criar',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível lançar a despesa.');
  }
}

// Verifica uma despesa de folha de pagamento (Salários, Horas Extra,
// Bónus, Alimentação, Segurança Social) antes de poder ser paga —
// segregação de funções: quem lançou não pode ser quem verifica (o
// backend re-valida isto de qualquer forma, ver
// ContasPagarRepository::verificarDespesa()).
export async function apiVerificarDespesaContaPagar(token, payload = {}) {
  try {
    const response = await api.put('/tesouraria/contas_pagar.php', {
      action: 'verificar',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data?.data?.item || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível verificar a despesa.');
  }
}

// ── Portal do Afiliado ───────────────────────────────────────────────────
// Estes endpoints (api_erp/afiliados/*.php) devolvem o payload sem o
// envelope 'data' que a maioria do resto da API usa — cada um tem as suas
// próprias chaves (dashboard, vendas, comissões, resumo, ...) — por isso
// usam um "ensure" próprio que devolve o payload inteiro, não payload.data.
function ensureAfiliadoPayload(response, fallbackMessage) {
  const payload = response?.data;
  const hasSuccessFlag = payload && typeof payload === "object" && Object.prototype.hasOwnProperty.call(payload, "success");

  if (hasSuccessFlag && payload?.success === false) {
    const explicitMessage = String(payload?.message || "").trim();
    const logicalError = new Error(explicitMessage || fallbackMessage);
    logicalError.response = { ...(response || {}), data: payload, status: response?.status };
    logicalError.apiPayload = payload;
    throw logicalError;
  }

  return payload || null;
}

export async function apiGetAfiliadoDashboard(token) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/dashboard.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar o teu painel de afiliado.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar o teu painel de afiliado.');
  }
}

export async function apiListAfiliadoVendas(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/vendas.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        estado: params?.estado || undefined,
        page: params?.page || undefined,
        limit: params?.limit || undefined,
      },
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar as vendas.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as vendas.');
  }
}

export async function apiGetAfiliadoVendaDetalhe(token, uuidComissao) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/vendas.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: { uuid: uuidComissao },
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar o detalhe da venda.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar o detalhe da venda.');
  }
}

export async function apiListAfiliadoComissoes(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/comissoes.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        estado: params?.estado || undefined,
        page: params?.page || undefined,
        limit: params?.limit || undefined,
      },
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar as comissões.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as comissões.');
  }
}

export async function apiGetAfiliadoCarteira(token) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/carteira.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar a carteira.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar a carteira.');
  }
}

export async function apiGetAfiliadoExtrato(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/carteira.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        secao: 'extrato',
        page: params?.page || undefined,
        limit: params?.limit || undefined,
      },
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar o extrato.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar o extrato.');
  }
}

export async function apiGetAfiliadoSaqueResumo(token) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/saques.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: { resumo: 1 },
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar os dados de saque.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os dados de saque.');
  }
}

export async function apiListAfiliadoSaques(token, params = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/saques.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: {
        estado: params?.estado || undefined,
        page: params?.page || undefined,
        limit: params?.limit || undefined,
      },
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar os saques.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os saques.');
  }
}

export async function apiGetAfiliadoSaqueDetalhe(token, uuidSaque) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/saques.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: { uuid: uuidSaque },
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar o detalhe do saque.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar o detalhe do saque.');
  }
}

export async function apiSolicitarAfiliadoSaque(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/afiliados/saques.php', payload, {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return ensureAfiliadoPayload(response, 'Não foi possível solicitar o saque.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível solicitar o saque.');
  }
}

export async function apiListAfiliadoMetodosPagamento(token) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/metodos_pagamento.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar os métodos de pagamento.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os métodos de pagamento.');
  }
}

export async function apiCreateAfiliadoMetodoPagamento(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.post('/afiliados/metodos_pagamento.php', payload, {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return ensureAfiliadoPayload(response, 'Não foi possível adicionar o método de pagamento.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível adicionar o método de pagamento.');
  }
}

export async function apiUpdateAfiliadoMetodoPagamento(token, uuidMetodo, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.put('/afiliados/metodos_pagamento.php', { ...payload, uuid_metodo: uuidMetodo }, {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return ensureAfiliadoPayload(response, 'Não foi possível atualizar o método de pagamento.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível atualizar o método de pagamento.');
  }
}

export async function apiDeleteAfiliadoMetodoPagamento(token, uuidMetodo) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.delete('/afiliados/metodos_pagamento.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
      params: { uuid: uuidMetodo },
    });
    return ensureAfiliadoPayload(response, 'Não foi possível remover o método de pagamento.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível remover o método de pagamento.');
  }
}

export async function apiGetAfiliadoPerfil(token) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.get('/afiliados/perfil.php', {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return ensureAfiliadoPayload(response, 'Não foi possível carregar o perfil.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar o perfil.');
  }
}

export async function apiUpdateAfiliadoPerfil(token, payload = {}) {
  try {
    const authorization = buildAuthorizationHeader(token);
    const response = await api.put('/afiliados/perfil.php', payload, {
      ...(authorization ? { headers: { Authorization: authorization } } : {}),
    });
    return ensureAfiliadoPayload(response, 'Não foi possível atualizar o perfil.');
  } catch (error) {
    throw buildApiError(error, 'Não foi possível atualizar o perfil.');
  }
}

// ── Programa de Afiliados — Administração (admin_super) ──────────────────
// api_erp/afiliados/admin.php — painel admin: gerir afiliados, saques,
// referências e configurações do programa.

export async function apiListAfiliadosAdmin(token, params = {}) {
  try {
    const response = await api.get('/afiliados/admin.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        recurso: 'lista',
        estado: params?.estado || undefined,
        search: params?.search || undefined,
        limit: params?.limit || undefined,
        offset: params?.offset || undefined,
      },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os afiliados.');
  }
}

export async function apiGetAfiliadoAdminDetalhe(token, uuidAfiliado) {
  try {
    const response = await api.get('/afiliados/admin.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: { recurso: 'detalhe', uuid: uuidAfiliado },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar o detalhe do afiliado.');
  }
}

export async function apiCreateAfiliado(token, payload) {
  try {
    const response = await api.post('/afiliados/admin.php', {
      acao: 'criar_afiliado',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível criar o afiliado.');
  }
}

export async function apiUpdateAfiliadoEstado(token, payload) {
  try {
    const response = await api.patch('/afiliados/admin.php', {
      acao: 'atualizar_estado',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível atualizar o estado do afiliado.');
  }
}

export async function apiUpdateAfiliadoTaxa(token, payload) {
  try {
    const response = await api.patch('/afiliados/admin.php', {
      acao: 'atualizar_taxa',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível atualizar a taxa de comissão.');
  }
}

export async function apiListSaquesAdmin(token, params = {}) {
  try {
    const response = await api.get('/afiliados/admin.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        recurso: 'saques',
        estado: params?.estado || undefined,
        limit: params?.limit || undefined,
        offset: params?.offset || undefined,
      },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar os pedidos de saque.');
  }
}

export async function apiReviewSaque(token, payload) {
  try {
    const response = await api.patch('/afiliados/admin.php', {
      acao: 'revisar_saque',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível rever o pedido de saque.');
  }
}

export async function apiGetAfiliadoConfiguracoes(token) {
  try {
    const response = await api.get('/afiliados/admin.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: { recurso: 'configuracoes' },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as configurações do programa.');
  }
}

export async function apiUpdateAfiliadoConfiguracoes(token, payload) {
  try {
    const response = await api.patch('/afiliados/admin.php', {
      acao: 'atualizar_configuracoes',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível atualizar as configurações do programa.');
  }
}

export async function apiListAfiliadoReferencias(token, uuidAfiliado) {
  try {
    const response = await api.get('/afiliados/admin.php', {
      headers: { Authorization: `Bearer ${token}` },
      params: { recurso: 'referencias', uuid: uuidAfiliado },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível carregar as empresas referidas.');
  }
}

export async function apiCreateReferenciaManual(token, payload) {
  try {
    const response = await api.post('/afiliados/admin.php', {
      acao: 'criar_referencia_manual',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível associar a empresa ao afiliado.');
  }
}

export async function apiCreateComissaoManual(token, payload) {
  try {
    const response = await api.post('/afiliados/admin.php', {
      acao: 'criar_comissao_manual',
      ...payload,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response?.data || null;
  } catch (error) {
    throw buildApiError(error, 'Não foi possível registar a comissão.');
  }
}

